import { Lesson, curve, ellipse, type LessonLevel, type OriginalSubject as Subject, type XY, K } from './drawing-lesson-brushes';

/**
 * 进阶与艺术版的独立构图。
 *
 * 这里不复用入门的线稿：每个题材在两个级别都重新决定视角、主体比例、场景和光源，
 * 再把材质步骤写进同一条笔迹时间线。**画面里不允许出现与题材无关的填充碎笔**——
 * v0.0.122 曾用一批按取模撒开的短笔“凑细节”，成品像蒙了一层灰，已整体回退。
 * 每一笔都要落在具体的东西上：云、水、光、材质或结构。
 */

const TAU = Math.PI * 2;
type Builder = (d: Lesson) => void;

/** 水平铺色带：天空、海面、桌面这类整幅底色，一笔横跨画面。 */
function band(d: Lesson, y: number, color: string, width = 54, opacity = 0.22, bend = 24) {
  d.stroke(curve([36, y], [[320, y - bend], [690, y + bend], [964, y]]), color, width, 'crayon', opacity, true);
}
/**
 * 整幅铺色：一组相接的宽笔把画布盖住，做出渐变底而不是一条条色带。
 * 条带式背景会在画面上留下一条条波浪边缘，v0.0.122 的“撒点”和这里的“条带”都是同一个毛病。
 */
function washField(d: Lesson, color: string, opacity: number, from = -40, to = 1040, step = 118, width = 152) {
  for (let y = from; y <= to; y += step) d.stroke(curve([-30, y], [[330, y - 34], [670, y + 34], [1030, y]]), color, width, 'crayon', opacity, true);
}
/** 软投影：同一条弧扫三层，越往外越淡，不留硬边。 */
function softShadow(d: Lesson, x: number, y: number, rx: number, color: string, opacity = 0.2) {
  for (let i = 0; i < 3; i++) {
    const half = rx * (1 - i * 0.22);
    d.stroke(curve([x - half, y + i * 8], [[x - half * 0.34, y - 9 + i * 8], [x + half * 0.34, y - 9 + i * 8], [x + half, y + i * 8]]), color, 34 - i * 8, 'crayon', opacity, true);
  }
}
/** 一圈花瓣：每片都是独立闭合形状，学习者能看清朝向、长度和遮挡。 */
function petalRing(d: Lesson, x: number, y: number, inner: number, outer: number, half: number, count: number, color: string, phase = 0) {
  const mid = (inner + outer) / 2, len = (outer - inner) / 2;
  for (let i = 0; i < count; i++) {
    const a = phase + i * TAU / count, dx = Math.cos(a), dy = Math.sin(a);
    d.shape(ellipse(mid, 0, len, half).map(([u, v]): XY => [x + u * dx - v * dy, y + u * dy + v * dx]), color);
  }
}
/** 云：三个相连的团加一条底边，比单个椭圆更像云。 */
function cloud(d: Lesson, x: number, y: number, r: number, color: string, shade: string, opacity = 0.3) {
  d.fill(ellipse(x - r * 0.95, y - r * 0.12, r * 0.74, r * 0.58), color);
  d.fill(ellipse(x + r * 0.86, y - r * 0.08, r * 0.68, r * 0.54), color);
  d.fill(ellipse(x, y - r * 0.52, r * 0.94, r * 0.74), color);
  d.fill([[x - r * 1.66, y + r * 0.42], [x + r * 1.52, y + r * 0.42], [x + r * 1.52, y - r * 0.06], [x - r * 1.66, y - r * 0.06]], color);
  d.stroke(curve([x - r * 1.44, y + r * 0.3], [[x - r * 0.5, y + r * 0.5], [x + r * 0.6, y + r * 0.5], [x + r * 1.44, y + r * 0.26]]), shade, r * 0.34, 'crayon', opacity, true);
}
/** 热气球球面经线：从顶点过赤道再收进气口，分瓣才是真的贴在球面上。 */
function meridian(cx: number, cyTop: number, r: number, f: number, mouthY: number): XY[] {
  const points: XY[] = [];
  for (let i = 0; i <= 20; i++) { const t = i / 20 * 0.78 * Math.PI; points.push([cx + r * Math.sin(t) * f, cyTop + r * (1 - Math.cos(t))]); }
  const y0 = cyTop + r * (1 - Math.cos(0.78 * Math.PI)), neck = r * Math.sin(0.78 * Math.PI);
  for (let i = 1; i <= 8; i++) { const s = i / 8; points.push([cx + f * (neck + (r * 0.2 - neck) * s), y0 + (mouthY - y0) * s]); }
  return points;
}
/** 两片经线之间的一瓣球面；相邻两瓣共边，铺完就是一整个气球。 */
function gore(cx: number, cyTop: number, r: number, f1: number, f2: number, mouthY: number): XY[] {
  return [...meridian(cx, cyTop, r, f1, mouthY), ...meridian(cx, cyTop, r, f2, mouthY).reverse()];
}
/** 草：三根长短不同的叶，落在同一条根上。 */
function grass(d: Lesson, x: number, y: number, height: number, color: string) {
  for (let i = -1; i <= 1; i++) d.stroke(curve([x, y], [[x + i * 9, y - height * 0.42], [x + i * 24, y - height * 0.8], [x + i * 38, y - height]]), color, 4 + (i + 1) * 2, 'ink');
}

// ───────────────────────────── 小猫 ─────────────────────────────

/** 进阶：软垫上的团身午睡。头压入肩背、尾巴围住身体，连贯轮廓交代蜷睡姿态。 */
const catAdvanced: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  const paint = (p: XY[], color: string, edge = color) => { d.fill(p, color); d.stroke([...p, p[0]], edge, 5, 'ink', 1, true); };
  d.drawing.background = t.paper;
  d.step('一只柔软的坐垫', '先画压扁的椭圆坐垫：下沿深、上面浅，中间留给蜷起来的小猫。');
  paint(ellipse(512, 768, 365, 108), c[4]);
  paint(ellipse(510, 741, 364, 88), c[3]);
  d.stroke(ellipse(510, 746, 345, 76, 0.12, 3.05), c[5], 4, 'ink', 0.8, true);
  d.stroke(curve([232, 790], [[386, 838], [650, 842], [804, 782]]), c[0], 18, 'translucent', 0.12, true);
  d.step('蜷起的身体', '先画圆弧背线，再把低下的头接进肩膀。两只耳朵长在同一条头顶轮廓上，头和身体不能分开。');
  const body = curve([322, 555], [[354, 442], [540, 403], [674, 456]], [[803, 494], [844, 621], [789, 714]], [[746, 792], [521, 800], [360, 748]], [[251, 718], [224, 620], [322, 555]]);
  paint(body, c[1], c[0]);
  const head = curve([242, 564], [[232, 515], [231, 443], [251, 420]], [[279, 431], [301, 457], [320, 469]], [[357, 452], [392, 452], [424, 460]], [[439, 440], [470, 405], [489, 401]], [[495, 447], [500, 499], [489, 542]], [[527, 615], [478, 692], [377, 705]], [[273, 716], [211, 653], [242, 564]]);
  paint(head, c[1], c[0]);
  paint(curve([254, 461], [[272, 470], [289, 492], [297, 511]], [[272, 518], [255, 526], [252, 542]], [[250, 511], [246, 483], [254, 461]]), t.light);
  paint(curve([447, 484], [[460, 466], [471, 450], [479, 444]], [[485, 479], [482, 506], [475, 522]], [[467, 508], [456, 491], [447, 484]]), t.light);
  d.step('奶油毛与虎斑', '口鼻的浅色合成一个完整的小面，额纹和背上的条纹顺着身体转弯，别平均撒满。');
  paint(curve([277, 615], [[282, 584], [326, 584], [359, 606]], [[397, 579], [451, 587], [466, 619]], [[483, 658], [427, 697], [375, 694]], [[319, 703], [277, 664], [277, 615]]), c[5]);
  paint(curve([487, 676], [[534, 645], [582, 658], [616, 694]], [[603, 727], [564, 754], [517, 749]], [[491, 735], [474, 704], [487, 676]]), c[5]);
  [[326, 484, 336, 543], [368, 474, 372, 532], [409, 481, 401, 540]].forEach(([x, y, a, b]) => d.stroke(curve([x, y], [[x - 5, y + 21], [a + 3, b - 15], [a, b]]), c[0], 13, 'ink', 0.78));
  d.stroke(curve([553, 452], [[548, 478], [562, 513], [585, 530]]), c[0], 20, 'ink', 0.75);
  d.stroke(curve([635, 469], [[624, 499], [647, 537], [670, 548]]), c[0], 22, 'ink', 0.75);
  d.stroke(curve([714, 516], [[691, 544], [709, 574], [734, 590]]), c[0], 20, 'ink', 0.75);
  d.stroke(curve([518, 481], [[586, 470], [649, 483], [698, 528]]), c[2], 25, 'translucent', 0.35);
  d.step('睡着的表情和小爪', '闭眼用两条下弯弧，鼻子落在口鼻中间。前爪搭在下巴边，短短两道趾缝就够了。');
  d.stroke(curve([277, 570], [[294, 591], [320, 591], [336, 572]]), c[0], 7, 'ink');
  d.stroke(curve([394, 567], [[409, 587], [434, 586], [449, 565]]), c[0], 7, 'ink');
  paint(curve([346, 611], [[357, 604], [374, 604], [386, 609]], [[384, 622], [372, 635], [366, 637]], [[357, 632], [349, 620], [346, 611]]), c[0]);
  d.stroke(curve([366, 636], [[365, 650], [351, 660], [341, 652]]), c[0], 4, 'ink');
  d.stroke(curve([366, 636], [[371, 650], [383, 656], [394, 648]]), c[0], 4, 'ink');
  [[291, 632, 213, 618], [294, 650, 216, 659], [439, 628, 501, 607], [443, 645, 506, 645]].forEach(([x, y, a, b]) => d.stroke([[x, y], [a, b]], c[0], 3, 'ink', 0.8));
  paint(curve([385, 700], [[410, 681], [461, 689], [480, 714]], [[485, 741], [451, 755], [414, 747]], [[386, 740], [371, 718], [385, 700]]), c[5], c[0]);
  d.stroke(curve([413, 722], [[410, 731], [413, 739], [418, 744]]), c[0], 3, 'ink');
  d.stroke(curve([435, 725], [[432, 734], [435, 742], [440, 747]]), c[0], 3, 'ink');
  d.step('尾巴围成一个抱抱', '尾巴从后腰弯到前面，沿身体绕一圈。最后在背和坐垫各留一小处柔光，画面就完成了。');
  const tail = curve([741, 596], [[818, 644], [812, 733], [738, 765]], [[662, 798], [538, 780], [493, 753]], [[467, 732], [488, 701], [519, 711]], [[574, 739], [666, 751], [714, 718]], [[758, 685], [750, 646], [722, 630]], [[706, 615], [722, 590], [741, 596]]);
  paint(tail, c[1], c[0]);
  d.stroke(curve([630, 750], [[636, 762], [636, 775], [633, 783]]), c[0], 17, 'ink', 0.78);
  d.stroke(curve([704, 738], [[715, 748], [719, 760], [720, 768]]), c[0], 17, 'ink', 0.78);
  d.stroke(curve([756, 696], [[768, 700], [781, 703], [789, 707]]), c[0], 16, 'ink', 0.78);
  d.stroke(curve([485, 467], [[544, 443], [602, 450], [646, 471]]), t.light, 17, 'crayon', 0.68);
  d.stroke(curve([310, 791], [[343, 802], [379, 809], [414, 812]]), c[5], 8, 'crayon', 0.7);
  // 团身是横向构图，整体上移，让上下留白均衡。
  for (const stroke of d.drawing.strokes) for (const point of stroke.points) point.y -= 112;
};

/** 艺术：月窗前仰望飞蛾的小猫。完整侧影、蓝夜暖毛与月光构成一幅安静的小故事。 */
const catArt: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  const paint = (p: XY[], color: string, edge = color) => { d.fill(p, color); d.stroke([...p, p[0]], edge, 5, 'ink', 1, true); };
  d.drawing.background = c[4];
  d.step('蓝夜与月窗', '蓝色夜空占大半张纸，暖月亮偏在右上，先为小猫仰望的方向留出空间。');
  paint(ellipse(714, 269, 142, 142), c[5]);
  d.stroke(ellipse(714, 269, 155, 155), t.light, 20, 'glow', 0.15, true);
  paint(curve([0, 783], [[191, 713], [357, 773], [515, 736]], [[751, 685], [875, 756], [1000, 693]], [[1000, 876], [300, 885], [0, 876]]), c[3]);
  d.step('窗框和木台', '窗框把蓝夜收进画里，窗台用一块暖木色托住主体，所有脚掌都要落在它上面。');
  paint([[54, 0], [112, 0], [112, 851], [54, 851]], c[0]);
  paint([[934, 0], [990, 0], [990, 851], [934, 851]], c[0]);
  paint([[34, 846], [1000, 846], [1000, 1000], [34, 1000]], t.shade);
  d.stroke([[43, 857], [993, 857]], t.light, 11, 'ink', 0.8, true);
  d.step('连成一体的侧影', '从耳尖一路画到额头、鼻尖和胸口，再落到前爪。背线往下鼓起，与后腿连成一个稳稳的坐姿。');
  const silhouette = curve([388, 442], [[369, 399], [358, 339], [365, 302]], [[395, 317], [423, 343], [440, 367]], [[471, 346], [502, 335], [529, 338]], [[542, 312], [562, 282], [581, 272]], [[589, 316], [594, 363], [586, 389]], [[614, 411], [632, 430], [644, 444]], [[660, 450], [679, 448], [690, 460]], [[699, 476], [680, 492], [659, 496]], [[650, 525], [628, 544], [597, 548]], [[583, 596], [579, 649], [588, 709]], [[599, 760], [623, 793], [640, 811]], [[654, 834], [632, 850], [590, 848]], [[563, 852], [529, 844], [508, 838]], [[449, 864], [367, 854], [326, 817]], [[284, 777], [308, 681], [329, 604]], [[343, 541], [367, 483], [388, 442]]);
  paint(silhouette, c[1], c[0]);
  d.step('胸毛与弯尾', '浅色从下巴连到前腿，尾巴贴着窗台向左弯。前后轮廓只在交叠处加深，不给每块都画黑圈。');
  paint(curve([601, 509], [[606, 542], [568, 568], [553, 618]], [[540, 681], [558, 763], [595, 811]], [[604, 825], [585, 838], [555, 830]], [[502, 780], [490, 682], [510, 610]], [[533, 557], [570, 534], [601, 509]]), c[5]);
  const tail = curve([350, 758], [[326, 813], [267, 850], [221, 814]], [[187, 788], [200, 752], [221, 736]], [[239, 722], [225, 700], [202, 710]], [[151, 733], [151, 807], [185, 842]], [[239, 902], [344, 867], [390, 805]], [[404, 780], [378, 750], [350, 758]]);
  paint(tail, c[1], c[0]);
  d.stroke(curve([395, 689], [[449, 681], [466, 750], [450, 803]]), c[0], 5, 'ink', 0.6);
  d.stroke(curve([571, 791], [[577, 803], [579, 817], [576, 833]]), c[0], 4, 'ink');
  d.step('抬头的眼睛', '侧脸只画一只杏仁眼，眼神朝着月光。鼻子小而靠前，胡须向外轻轻展开。');
  paint(curve([394, 396], [[383, 373], [379, 349], [378, 336]], [[400, 351], [418, 370], [427, 383]], [[414, 385], [403, 390], [394, 396]]), t.light);
  paint(curve([546, 366], [[554, 347], [565, 325], [575, 310]], [[580, 336], [580, 362], [573, 379]], [[563, 372], [553, 369], [546, 366]]), t.light);
  paint(curve([551, 429], [[568, 402], [597, 404], [611, 424]], [[593, 442], [568, 448], [551, 429]]), c[2], c[0]);
  d.stroke([[588, 416], [586, 435]], c[0], 10, 'ink');
  d.dot(594, 417, 7, c[5]);
  paint([[674, 455], [690, 461], [679, 474], [669, 465]], c[0]);
  d.stroke(curve([659, 496], [[643, 498], [633, 500], [624, 495]]), c[0], 4, 'ink');
  [[640, 499, 730, 484], [635, 512, 721, 524], [633, 523, 707, 552]].forEach(([x, y, a, b]) => d.stroke([[x, y], [a, b]], c[5], 3, 'ink', 0.85));
  d.step('顺着毛势的花纹', '额纹贴着头顶往后走，肩背花纹顺着圆弧往下转。暗部集中在背光侧，胸前的白毛保持干净。');
  d.stroke(curve([438, 379], [[458, 395], [466, 412], [469, 431]]), c[0], 15, 'ink', 0.65);
  d.stroke(curve([480, 357], [[498, 378], [504, 393], [505, 410]]), c[0], 15, 'ink', 0.7);
  d.stroke(curve([367, 522], [[398, 528], [418, 548], [428, 570]]), c[0], 21, 'ink', 0.6);
  d.stroke(curve([345, 600], [[377, 604], [398, 622], [408, 646]]), c[0], 24, 'ink', 0.6);
  d.stroke(curve([325, 678], [[354, 682], [368, 699], [374, 722]]), c[0], 23, 'ink', 0.6);
  d.stroke(curve([312, 769], [[326, 798], [347, 805], [367, 806]]), c[0], 20, 'translucent', 0.24);
  d.stroke(curve([357, 562], [[329, 649], [316, 731], [334, 777]]), c[0], 28, 'translucent', 0.13);
  d.step('月光与一只飞蛾', '在面向月亮的鼻梁、胸口和耳缘留细细的暖光，月下加一只小飞蛾，视线就有了落点。');
  d.stroke(curve([585, 388], [[614, 411], [632, 431], [644, 444]]), t.light, 8, 'glow', 0.7);
  d.stroke(curve([593, 552], [[574, 598], [574, 641], [579, 678]]), t.light, 9, 'crayon', 0.7);
  d.stroke([[561, 308], [580, 279]], c[5], 5, 'glow', 0.65);
  paint(curve([752, 505], [[720, 474], [727, 452], [744, 474]], [[751, 485], [753, 496], [752, 505]]), c[5]);
  paint(curve([754, 505], [[760, 468], [785, 456], [780, 482]], [[774, 496], [765, 503], [754, 505]]), t.light);
  d.stroke([[752, 495], [756, 518]], c[2], 4, 'ink');
  d.stroke(curve([431, 898], [[578, 917], [730, 903], [857, 915]]), c[0], 5, 'ink', 0.25, true);
};

// ───────────────────────────── 熊猫 ─────────────────────────────

/** 进阶：抱竹用餐。双爪真正跨过竹竿，耳根、肩与头的遮挡共同建立坐姿。 */
const pandaAdvanced: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  const paint = (p: XY[], color: string, edge = color) => { d.fill(p, color); d.stroke([...p, p[0]], edge, 5, 'ink', 1, true); };
  const leaf = (x: number, y: number, dx: number, dy: number) => { paint(curve([x, y], [[x + dx * 0.25 - dy * 0.13, y + dy * 0.25 + dx * 0.13], [x + dx * 0.8 - dy * 0.11, y + dy * 0.8 + dx * 0.11], [x + dx, y + dy]], [[x + dx * 0.6 + dy * 0.1, y + dy * 0.6 - dx * 0.1], [x + dx * 0.2 + dy * 0.12, y + dy * 0.2 - dx * 0.12], [x, y]]), c[3]); d.stroke([[x + dx * 0.23, y + dy * 0.23], [x + dx * 0.77, y + dy * 0.77]], c[3], Math.min(10, Math.hypot(dx, dy) * 0.09), 'pen', 1, true); };
  d.drawing.background = t.paper;
  d.step('安静的草地', '先铺一块低低的草地，远竹留在左侧。主体周围留白，抱竹的动作才清楚。');
  paint(curve([80, 802], [[267, 751], [746, 760], [908, 825]], [[899, 875], [229, 897], [80, 842]], [[73, 831], [73, 816], [80, 802]]), c[2]);
  d.stroke(curve([295, 846], [[420, 867], [622, 865], [731, 830]]), t.shade, 24, 'translucent', 0.34, true);
  d.stroke([[147, 757], [181, 245]], t.shade, 13, 'ink', 0.5, true);
  d.stroke([[183, 741], [210, 181]], c[3], 9, 'ink', 0.3, true);
  leaf(174, 380, -70, -50); leaf(179, 321, 71, -42); leaf(156, 587, -64, -44);
  d.step('头压在肩上的坐姿', '身体下宽上窄，圆头压住肩线。耳朵先画、脸后画，耳根自然藏到头后面。');
  paint(curve([410, 498], [[459, 463], [563, 475], [609, 520]], [[678, 587], [701, 734], [658, 794]], [[605, 861], [364, 855], [313, 777]], [[276, 706], [334, 566], [410, 498]]), c[5], c[0]);
  paint(curve([346, 542], [[382, 486], [437, 472], [492, 490]], [[555, 467], [616, 509], [642, 559]], [[599, 601], [410, 607], [346, 542]]), K);
  paint(ellipse(358, 305, 59, 62), K);
  paint(ellipse(606, 294, 60, 62), K);
  const face = curve([332, 365], [[348, 296], [425, 273], [497, 286]], [[573, 270], [631, 309], [645, 370]], [[670, 440], [626, 514], [555, 543]], [[471, 578], [383, 549], [342, 494]], [[313, 455], [307, 405], [332, 365]]);
  paint(face, c[5], c[0]);
  paint(curve([312, 749], [[275, 740], [251, 774], [263, 813]], [[279, 854], [364, 870], [403, 838]], [[418, 817], [395, 773], [369, 759]], [[350, 748], [330, 746], [312, 749]]), K);
  paint(curve([592, 786], [[617, 750], [675, 732], [704, 761]], [[741, 799], [705, 844], [660, 858]], [[607, 877], [554, 836], [592, 786]]), K);
  d.step('斜着的一枝嫩竹', '竹子从膝前斜到嘴边，先画竹干与竹节，再让细长的叶子向外舒展，叶子不要盖住脸。');
  d.stroke([[454, 822], [651, 338]], c[3], 27, 'pen', 1, true);
  d.stroke([[461, 811], [655, 342]], c[2], 6, 'ink', 0.8, true);
  [[491, 731], [533, 627], [575, 525], [616, 422]].forEach(([x, y]) => d.stroke([[x - 14, y - 6], [x + 15, y + 6]], t.accent, 6, 'ink'));
  d.stroke(curve([625, 400], [[710, 400], [757, 372], [814, 307]]), c[3], 5, 'ink');
  leaf(700, 391, 46, -90); leaf(728, 379, 84, -12); leaf(762, 350, 14, -81); leaf(790, 326, 67, -28);
  d.stroke(curve([552, 580], [[625, 589], [684, 566], [727, 524]]), c[3], 4, 'ink');
  leaf(637, 577, 48, 50); leaf(669, 563, 71, -12); leaf(699, 549, 19, -61);
  d.step('抱住竹子的双爪', '两条胳膊从肩膀弯进来，手掌盖在竹干上。眼圈倾斜一点，视线就落到正在吃的叶子上。');
  paint(curve([350, 548], [[310, 561], [306, 624], [352, 667]], [[397, 705], [493, 695], [542, 649]], [[571, 625], [552, 589], [526, 594]], [[480, 616], [423, 629], [401, 607]], [[390, 572], [374, 547], [350, 548]]), K);
  paint(curve([610, 542], [[658, 523], [691, 557], [675, 602]], [[662, 643], [626, 673], [591, 648]], [[566, 627], [572, 593], [603, 581]], [[615, 577], [620, 557], [610, 542]]), K);
  const eyePatch = (x: number, y: number, angle: number) => ellipse(0, 0, 35, 49).map(([a, b]): XY => [x + a * Math.cos(angle) - b * Math.sin(angle), y + a * Math.sin(angle) + b * Math.cos(angle)]);
  paint(eyePatch(411, 408, 0.42), K); paint(eyePatch(559, 400, -0.34), K);
  d.dot(422, 404, 20, c[5]); d.dot(563, 401, 20, c[5]);
  d.dot(427, 406, 11, K); d.dot(568, 402, 11, K);
  paint(curve([477, 460], [[490, 451], [511, 450], [526, 460]], [[522, 476], [503, 487], [495, 484]], [[484, 478], [478, 469], [477, 460]]), K);
  d.stroke(curve([501, 484], [[501, 503], [517, 509], [531, 497]]), c[0], 5, 'ink');
  d.stroke(curve([501, 484], [[498, 501], [483, 505], [474, 496]]), c[0], 5, 'ink');
  d.stroke([[530, 496], [571, 488]], c[3], 7, 'ink');
  leaf(552, 491, 38, -22);
  d.step('毛边与轻轻的亮面', '白毛只在背光边轻扫一层灰绿，黑爪用短弧提示手指。草地上放两片落叶，保持画面清爽。');
  d.stroke(curve([611, 453], [[602, 487], [579, 509], [547, 520]]), t.light, 18, 'translucent', 0.42);
  d.stroke(curve([390, 698], [[405, 748], [442, 782], [478, 786]]), t.light, 22, 'crayon', 0.56);
  d.stroke(curve([461, 659], [[483, 654], [502, 643], [515, 632]]), t.shade, 5, 'ink', 0.6);
  d.stroke(curve([621, 598], [[613, 611], [609, 622], [611, 633]]), t.shade, 5, 'ink', 0.65);
  d.stroke(curve([280, 792], [[290, 785], [299, 785], [307, 789]]), t.shade, 5, 'ink', 0.6);
  d.stroke(curve([306, 804], [[316, 796], [326, 797], [334, 801]]), t.shade, 5, 'ink', 0.6);
  d.stroke([[357, 472], [379, 480]], c[1], 12, 'translucent', 0.45);
  leaf(782, 801, 64, -31); leaf(234, 839, -52, -23);
};

/** 艺术：薄雾竹林里的树上小憩。长背伏在斜枝上，低头与垂爪给出自然的重量。 */
const pandaArt: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  const paint = (p: XY[], color: string, edge = color) => { d.fill(p, color); d.stroke([...p, p[0]], edge, 5, 'ink', 1, true); };
  const leaf = (x: number, y: number, dx: number, dy: number, color = c[3]) => { paint(curve([x, y], [[x + dx * 0.22 - dy * 0.13, y + dy * 0.22 + dx * 0.13], [x + dx * 0.8 - dy * 0.1, y + dy * 0.8 + dx * 0.1], [x + dx, y + dy]], [[x + dx * 0.6 + dy * 0.1, y + dy * 0.6 - dx * 0.1], [x + dx * 0.2 + dy * 0.1, y + dy * 0.2 - dx * 0.1], [x, y]]), color); d.stroke([[x + dx * 0.23, y + dy * 0.23], [x + dx * 0.77, y + dy * 0.77]], color, Math.min(10, Math.hypot(dx, dy) * 0.09), 'pen', 1, true); };
  d.drawing.background = t.paper;
  d.step('薄雾里的留白', '浅青绿做整幅底色，远坡压在下面，中央留一大块清亮的空气，让午睡显得安静。');
  paint(curve([0, 760], [[236, 601], [414, 751], [608, 630]], [[815, 494], [916, 567], [1000, 558]], [[1000, 1000], [0, 1000], [0, 760]]), t.light);
  paint(curve([0, 910], [[180, 799], [345, 884], [512, 794]], [[736, 669], [906, 714], [1000, 660]], [[1000, 1000], [0, 1000], [0, 910]]), c[2]);
  d.step('疏落的远竹', '远竹只在两侧出现，颜色轻、竿子细。竹叶是一组组细尖叶片，中间要留透气的空隙。');
  [[122, 170, 30], [204, 105, -10], [881, 88, -27], [947, 148, -15]].forEach(([x, y, dx]) => {
    d.stroke([[x, 876], [x + dx, y]], t.shade, 15, 'ink', 0.48, true);
    for (let yy = y + 150; yy < 890; yy += 174) d.stroke([[x - 6 + dx * 0.4, yy], [x + 14 + dx * 0.4, yy - 2]], c[3], 4, 'ink', 0.4);
  });
  leaf(162, 348, -62, -91, t.shade); leaf(170, 371, 78, -65, t.shade); leaf(187, 302, 31, -104, t.shade);
  leaf(865, 263, -76, -77, t.shade); leaf(866, 290, 70, -78, t.shade); leaf(884, 215, -16, -103, t.shade);
  d.step('一根结实的斜树干', '树干从左下托到右上，先画承重的大形再添分枝。上面受光、下面稍暗，熊猫才能稳稳趴住。');
  const branch = curve([49, 811], [[337, 739], [633, 618], [978, 500]], [[1000, 520], [1000, 563], [982, 578]], [[665, 675], [361, 810], [61, 886]], [[48, 866], [42, 832], [49, 811]]);
  paint(branch, c[3], t.accent);
  d.stroke(curve([71, 820], [[337, 751], [654, 631], [976, 517]]), c[2], 14, 'crayon', 0.85, true);
  d.stroke(curve([101, 861], [[377, 785], [679, 667], [971, 561]]), t.accent, 11, 'ink', 0.5, true);
  d.stroke(curve([717, 621], [[764, 553], [808, 490], [815, 393]]), c[3], 30, 'ink', 1, true);
  d.stroke(curve([851, 558], [[890, 552], [928, 563], [968, 598]]), c[3], 20, 'ink', 1, true);
  d.step('趴着的身体和低头', '背线是一条舒展的长弧，肚子伏在树干上，头低下来枕住前爪。先画耳朵，再用脸盖住耳根。');
  paint(curve([405, 490], [[464, 414], [638, 399], [727, 439]], [[786, 466], [806, 513], [790, 550]], [[763, 606], [619, 637], [509, 650]], [[433, 649], [388, 575], [405, 490]]), c[5], c[0]);
  paint(curve([663, 427], [[727, 430], [782, 465], [796, 507]], [[808, 543], [768, 578], [731, 584]], [[699, 578], [697, 534], [689, 499]], [[680, 475], [669, 449], [663, 427]]), K);
  paint(ellipse(324, 438, 49, 52), K);
  paint(ellipse(505, 458, 47, 49), K);
  const face = curve([283, 496], [[311, 448], [381, 439], [438, 460]], [[511, 475], [539, 530], [521, 586]], [[505, 642], [447, 680], [381, 679]], [[310, 677], [266, 643], [263, 587]], [[260, 552], [269, 521], [283, 496]]);
  paint(face, c[5], c[0]);
  d.step('垂下的爪和睡眼', '前臂绕过树干垂下来，后脚轻轻搭在右边。眼圈沿脸的方向倾斜，闭眼的浅弧只留一点点。');
  paint(curve([466, 566], [[514, 547], [554, 576], [544, 622]], [[535, 666], [539, 721], [513, 751]], [[489, 777], [456, 761], [453, 732]], [[455, 690], [452, 650], [446, 621]], [[443, 596], [450, 577], [466, 566]]), K);
  paint(curve([356, 643], [[385, 627], [418, 640], [423, 666]], [[429, 691], [395, 711], [358, 708]], [[324, 706], [317, 674], [356, 643]]), K);
  paint(curve([748, 520], [[794, 510], [822, 541], [806, 575]], [[788, 609], [803, 647], [782, 666]], [[760, 689], [730, 674], [731, 646]], [[735, 612], [719, 550], [748, 520]]), K);
  const patch = (x: number, y: number, angle: number) => ellipse(0, 0, 30, 43).map(([a, b]): XY => [x + a * Math.cos(angle) - b * Math.sin(angle), y + a * Math.sin(angle) + b * Math.cos(angle)]);
  paint(patch(327, 554, 0.55), K); paint(patch(442, 577, -0.48), K);
  d.stroke(curve([310, 550], [[318, 562], [330, 566], [340, 560]]), c[5], 4, 'ink');
  d.stroke(curve([425, 575], [[434, 588], [446, 591], [456, 585]]), c[5], 4, 'ink');
  paint(curve([356, 610], [[369, 602], [385, 607], [393, 617]], [[385, 631], [373, 632], [367, 626]], [[363, 621], [359, 615], [356, 610]]), K);
  d.stroke(curve([373, 631], [[369, 644], [383, 652], [394, 645]]), c[0], 4, 'ink');
  d.step('树梢的细叶', '枝头几束叶子把画面向上展开，叶片顺着同一根细枝长。不要让叶子遮住表情和垂下的爪。');
  d.stroke(curve([813, 437], [[753, 383], [714, 352], [670, 345]]), c[3], 6, 'ink');
  leaf(775, 399, 8, -115); leaf(754, 383, -71, -83); leaf(727, 362, -82, -18); leaf(801, 419, 75, -77);
  d.stroke(curve([850, 558], [[882, 486], [924, 442], [979, 412]]), c[3], 5, 'ink');
  leaf(901, 469, -6, -92); leaf(924, 446, 64, -14); leaf(948, 431, -6, -74);
  d.stroke(curve([62, 829], [[81, 717], [113, 670], [163, 627]]), t.accent, 6, 'ink');
  leaf(102, 706, -50, -72, t.accent); leaf(122, 673, 67, -34, t.accent); leaf(149, 641, 7, -75, t.accent);
  d.step('安静的午后光', '光只落在背上和头顶，用浅色弧顺着毛面扫过。树皮添两道长纹，末尾在草坡上轻落一片竹叶。');
  d.stroke(curve([555, 445], [[588, 439], [621, 441], [650, 448]]), c[2], 18, 'translucent', 0.32);
  d.stroke(curve([310, 480], [[345, 460], [385, 461], [416, 476]]), t.light, 13, 'crayon', 0.7);
  d.stroke(curve([581, 589], [[620, 580], [652, 568], [678, 553]]), t.shade, 16, 'translucent', 0.22);
  d.stroke(curve([477, 716], [[486, 728], [499, 730], [507, 722]]), t.shade, 5, 'ink', 0.6);
  d.stroke(curve([204, 811], [[264, 793], [311, 773], [352, 762]]), t.accent, 4, 'ink', 0.7);
  d.stroke(curve([592, 684], [[649, 662], [688, 649], [722, 638]]), t.accent, 4, 'ink', 0.7);
  d.stroke([[310, 606], [330, 614]], c[1], 11, 'translucent', 0.38);
  leaf(721, 864, 68, -37, t.shade);
};

// ───────────────────────────── 向日葵 ─────────────────────────────

/** 进阶：青釉陶壶里的单枝向日葵。花冠、弯茎与壶口形成一条清楚的生长线。 */
const sunflowerAdvanced: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  d.drawing.background = c[5];
  const paint = (path: XY[], color: string, edge = color) => { d.fill(path, color); d.stroke([...path, path[0]], edge, 5, 'ink'); };
  const leaf = (x: number, y: number, dx: number, dy: number) => {
    const p = curve([x, y], [[x + dx * 0.15 - dy * 0.48, y + dy * 0.15 + dx * 0.48], [x + dx * 0.8 - dy * 0.32, y + dy * 0.8 + dx * 0.32], [x + dx, y + dy]], [[x + dx * 0.7 + dy * 0.32, y + dy * 0.7 - dx * 0.32], [x + dy * 0.35, y - dx * 0.35], [x, y]]);
    paint(p, c[3]);
    d.stroke(curve([x, y], [[x + dx * 0.32, y + dy * 0.24], [x + dx * 0.68, y + dy * 0.64], [x + dx * 0.9, y + dy * 0.9]]), t.accent, 5, 'ink');
  };
  d.step('青釉陶壶', '先画壶把，再画鼓起来的壶身遮住接头；壶口用扁椭圆，左侧留一道釉光。');
  d.stroke(curve([353, 899], [[452, 922], [692, 928], [771, 894]]), t.shade, 34, 'crayon', 0.18, true);
  d.stroke(curve([620, 709], [[779, 651], [791, 833], [637, 842]]), c[4], 27, 'ink');
  d.stroke(curve([640, 710], [[751, 683], [756, 808], [654, 823]]), t.accent, 7, 'ink', 0.8);
  paint(curve([427, 675], [[412, 730], [358, 786], [401, 857]], [[440, 914], [609, 919], [659, 866]], [[695, 811], [651, 735], [625, 675]], [[566, 690], [483, 690], [427, 675]]), c[4]);
  d.stroke(curve([424, 752], [[399, 797], [415, 855], [455, 872]]), c[5], 24, 'translucent', 0.34);
  d.stroke(curve([472, 864], [[513, 890], [582, 883], [620, 858]]), c[3], 15, 'crayon', 0.6);
  paint(ellipse(525, 676, 101, 22), c[0]);
  d.stroke(ellipse(525, 681, 101, 23, 0, Math.PI), t.accent, 7, 'ink');
  d.step('弯茎和心形叶', '茎从壶口向右上弯；两片心形叶一高一低，叶脉顺着叶尖走。');
  d.stroke(curve([524, 678], [[543, 548], [639, 439], [675, 352]]), c[3], 20, 'ink');
  d.stroke(curve([518, 655], [[547, 548], [633, 429], [669, 368]]), t.accent, 6, 'ink');
  d.stroke(curve([547, 583], [[489, 582], [450, 565], [404, 539]]), c[3], 10, 'ink');
  leaf(428, 553, -188, -112);
  d.stroke(curve([569, 516], [[603, 507], [627, 497], [644, 482]]), c[3], 9, 'ink');
  leaf(619, 496, 168, -62);
  d.step('长短相间的花瓣', '花瓣从同一个花心向外长，后排深、前排亮；尖端轻轻弯，长度不要完全相同。');
  const cx = 679, cy = 299;
  const petals: { a: number; length: number }[] = [];
  for (let ring = 0; ring < 2; ring++) for (let i = 0; i < 13; i++) {
    const a = i * TAU / 13 + ring * 0.23, length = (ring ? 182 : 220) + Math.sin(i * 2.1) * 13;
    const local = curve([65, -17], [[112, -41], [length - 32, -38], [length, -8]], [[length - 21, 21], [113, 40], [65, 20]]);
    const p = local.map(([x, y]): XY => [cx + x * Math.cos(a) - y * Math.sin(a), cy + x * Math.sin(a) + y * Math.cos(a)]);
    paint(p, ring ? c[2] : t.shade);
    if (ring) petals.push({ a, length });
  }
  d.step('棕色花盘', '花盘盖住花瓣根部；先铺深棕，再用弧形排线交错画出密实的种子。');
  paint(ellipse(cx, cy, 90, 86), c[0]);
  d.stroke(ellipse(cx, cy, 73, 69, 2.8, 4.9), c[1], 18, 'crayon', 0.75);
  for (let row = -2; row <= 2; row++) for (let col = -3; col <= 3; col++) {
    const x = col * 20 + (row % 2) * 8, y = row * 23;
    if (x * x + y * y > 65 ** 2) continue;
    d.stroke([[cx + x - 3, cy + y - 5], [cx + x + 3, cy + y + 5]], t.shade, 6, 'ink');
  }
  d.step('花瓣里的光', '每片花瓣只画一道顺形细脉；左上朝光的一侧提亮，右下的瓣根轻压暖色。');
  for (const { a, length } of petals) {
    const project = (u: number, v: number): XY => [cx + u * Math.cos(a) - v * Math.sin(a), cy + u * Math.sin(a) + v * Math.cos(a)];
    d.stroke(curve(project(99, 2), [project(123, -4), project(length - 44, -7), project(length - 18, -6)]), a > 2.8 && a < 5.1 ? t.light : c[1], 4, 'ink', 0.7);
    if (a < 2.7) d.stroke([project(101, 8), project(137, 9)], c[1], 13, 'translucent', 0.2);
  }
};

/** 艺术：逆光特写。一朵花冲出画面四边，花心最暗，朝光的瓣尖镶一条亮线。 */
const sunflowerArt: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  d.drawing.background = c[4];
  d.step('暗底与来光', '整幅压成深青，左上角扫一块暖光——黄花在暗底上才亮得起来。');
  washField(d, c[4], 0.4, -40, 1060, 126, 170);
  washField(d, c[1], 0.14, -40, 380, 120, 168);
  d.stroke(curve([-30, 60], [[240, 20], [420, 120], [560, 260]]), c[2], 170, 'glow', 0.22, true);
  d.step('花瓣冲出画面', '花心偏右下，花瓣向四面长到画外；每片长短不一，才不像刻度盘。');
  petalRing(d, 560, 470, 190, 520, 42, 15, c[2], 0.12);
  d.step('花心的层次', '花心是全画最暗的地方：外圈管状小花一圈点，中间种子螺旋排。');
  d.shape(ellipse(560, 470, 196, 188), c[0]);
  for (let i = 0; i < 20; i++) { const a = i / 20 * TAU; d.dot(560 + Math.cos(a) * 166, 470 + Math.sin(a) * 160, 10, c[1]); }
  for (let i = 0; i < 24; i++) { const a = i * 2.399, rr = Math.sqrt(i) * 24; d.dot(560 + Math.cos(a) * rr, 470 + Math.sin(a) * rr, 8, c[1]); }
  d.step('花瓣的明暗', '背光的右下压深，受光的左上留着，花瓣才有厚度。');
  d.stroke(ellipse(560, 470, 260, 250, 0.5, 2.4), c[1], 90, 'translucent', 0.3);
  d.stroke(ellipse(560, 470, 330, 316, 0.7, 2.0), c[0], 70, 'translucent', 0.28);
  d.step('瓣尖的镶光', '光从左上打过来，只在朝光的瓣尖描一小段亮线，别整圈描。');
  for (let i = 0; i < 15; i++) {
    const a = 0.12 + i * TAU / 15;
    if (a < 2.5 || a > 4.7) continue;
    const dx = Math.cos(a), dy = Math.sin(a);
    d.stroke([[560 + dx * 425, 470 + dy * 425], [560 + dx * 516, 470 + dy * 516]], c[5], 9, 'glow', 0.65);
  }
  d.step('茎叶剪影', '左下压一段茎和一片叶的深色剪影，只在边缘透一点暖色。');
  d.stroke(curve([236, 1040], [[258, 900], [330, 800], [424, 736]]), K, 40, 'ink');
  d.shape(curve([110, 1010], [[164, 880], [258, 822], [352, 800]], [[300, 902], [216, 972], [140, 1010]]), K, K);
  d.stroke(curve([146, 990], [[198, 892], [280, 840], [344, 820]]), c[1], 8, 'ink', 0.7);
  d.step('空气里的花粉', '喷枪只在花心周围散几处，越远越稀。');
  [[380, 330], [762, 306], [836, 566], [348, 624], [656, 712]].forEach(([x, y], i) => d.stroke([[x, y], [x + 20, y - 12]], i % 2 ? t.light : c[2], 20 - i * 2, 'spray', 0.32));
};

// ───────────────────────────── 草莓 ─────────────────────────────

/** 进阶：白瓷碟上的两颗鲜果。完整的果形、贴地投影与两个朝向共同说明体积。 */
const strawberryAdvanced: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  d.drawing.background = t.paper;
  const paint = (path: XY[], color: string, edge = color) => { d.fill(path, color); d.stroke([...path, path[0]], edge, 5, 'ink'); };
  const fruit = (x: number, y: number, rx: number, ry: number, angle: number) => {
    const map = ([u, v]: XY): XY => [x + u * Math.cos(angle) - v * Math.sin(angle), y + u * Math.sin(angle) + v * Math.cos(angle)];
    const path = curve([0, -ry * 0.76], [[-rx * 0.81, -ry * 1.07], [-rx * 1.12, -ry * 0.44], [-rx * 0.79, ry * 0.1]], [[-rx * 0.57, ry * 0.6], [-rx * 0.2, ry * 0.95], [0, ry]], [[rx * 0.23, ry * 0.98], [rx * 0.7, ry * 0.53], [rx * 0.87, -ry * 0.06]], [[rx * 1.17, -ry * 0.62], [rx * 0.63, -ry * 1.03], [0, -ry * 0.76]]).map(map);
    return { map, path, rx, ry };
  };
  const back = fruit(596, 555, 180, 235, -0.58), front = fruit(338, 648, 125, 173, -0.95);
  const shade = (f: typeof back) => {
    const { map, rx, ry } = f;
    d.stroke(curve(map([rx * 0.68, -ry * 0.36]), [map([rx * 0.84, ry * 0.13]), map([rx * 0.4, ry * 0.69]), map([rx * 0.02, ry * 0.89])]), t.shade, rx * 0.29, 'translucent', 0.45);
    d.stroke(curve(map([-rx * 0.51, -ry * 0.48]), [map([-rx * 0.72, -ry * 0.19]), map([-rx * 0.52, ry * 0.2]), map([-rx * 0.32, ry * 0.4])]), t.light, rx * 0.23, 'translucent', 0.55);
  };
  d.step('白瓷小碟', '先画盘底投影，再用两个扁椭圆区分盘沿与盘心；近处的盘沿略厚。');
  d.stroke(curve([171, 837], [[350, 901], [685, 904], [856, 819]]), c[4], 36, 'crayon', 0.2, true);
  paint(ellipse(509, 742, 365, 139), c[5]);
  d.stroke(ellipse(509, 742, 365, 139), c[4], 7, 'ink', 0.72);
  d.stroke(ellipse(509, 742, 307, 101), c[4], 11, 'translucent', 0.3);
  d.stroke(ellipse(509, 749, 355, 133, 0.25, 2.75), c[5], 8, 'ink');
  d.step('斜卧的后果', '后面的草莓向右下斜卧；两边肩膀不完全对称，右侧压暗、左侧留光。');
  d.stroke(curve([643, 746], [[681, 763], [735, 763], [772, 750]]), t.shade, 18, 'translucent', 0.24);
  paint(back.path, c[1], t.shade); shade(back);
  d.step('斜躺的前果', '前果横着躺下，果尖朝右下，盖住后果左下的一小块；遮挡边不用重复描线。');
  d.stroke(curve([287, 753], [[344, 779], [437, 779], [484, 755]]), t.shade, 20, 'translucent', 0.24);
  paint(front.path, c[1], t.shade); shade(front);
  d.step('贴着果肩的叶冠', '五片萼叶都从果柄的同一点伸出，贴着肩部展开；不要让叶子浮在果子上面。');
  for (const f of [back, front]) {
    const { map, rx, ry } = f, top = -ry * 0.79;
    const ends: XY[] = [[-rx * 0.87, top + 25], [-rx * 0.56, top - 60], [4, top - 83], [rx * 0.63, top - 51], [rx * 0.91, top + 37]];
    ends.forEach(([u, v], i) => {
      const start: XY = [0, top + 8], end: XY = [u, v], dx = u, dy = v - start[1];
      const p = curve(start, [[dx * 0.22 - dy * 0.16, start[1] + dy * 0.22 + dx * 0.16], [u - dy * 0.18, v + dx * 0.18], end], [[u + dy * 0.18, v - dx * 0.18], [dx * 0.2 + dy * 0.16, start[1] + dy * 0.2 - dx * 0.16], start]).map(map);
      paint(p, i % 2 ? t.accent : c[3]);
      d.stroke([map(start), map([u * 0.82, start[1] + dy * 0.82])], c[3], 3, 'ink');
    });
    d.stroke(curve(map([0, top + 7]), [map([-10, top - 24]), map([0, top - 48]), map([24, top - 64])]), c[3], 11, 'ink');
  }
  d.step('籽窝与亮面', '小籽分成错开的弧形行，越接近果尖越少；高光只留在受光的一边。');
  for (const f of [back, front]) {
    const { map, rx, ry } = f;
    [5, 5, 4, 3, 2].forEach((count, row) => {
      const v = -0.47 + row * 0.28, span = [0.59, 0.65, 0.56, 0.38, 0.2][row];
      for (let i = 0; i < count; i++) {
        const u = (i / (count - 1) * 2 - 1) * span + (row % 2 ? 0.035 : 0);
        const at = map([u * rx, v * ry]), to = map([u * rx - u * 6, v * ry + 21]);
        d.stroke([at, to], t.shade, 12, 'ink', 0.55);
        d.stroke([map([u * rx - 2, v * ry - 1]), map([u * rx - u * 6 - 2, v * ry + 17])], c[2], 5, 'ink');
      }
    });
    d.stroke(curve(map([-rx * 0.47, -ry * 0.54]), [map([-rx * 0.57, -ry * 0.48]), map([-rx * 0.61, -ry * 0.35]), map([-rx * 0.6, -ry * 0.27])]), c[5], 9, 'ink', 0.9);
  }
};

/** 艺术：果园植物肖像。深青衬底，白花、小青果与成熟红果呈现同一枝的生长。 */
const strawberryArt: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  const dark = '#2F5A5E', leafDark = '#12261E';
  d.drawing.background = dark;
  const paint = (path: XY[], color: string, edge = color) => { d.fill(path, color); d.stroke([...path, path[0]], edge, 5, 'ink'); };
  const center: XY = [605, 582], angle = 0.19;
  const map = ([u, v]: XY): XY => [center[0] + u * Math.cos(angle) - v * Math.sin(angle), center[1] + u * Math.sin(angle) + v * Math.cos(angle)];
  const berry = curve([0, -218], [[-175, -284], [-264, -145], [-188, 20]], [[-148, 148], [-57, 278], [0, 291]], [[72, 288], [181, 119], [207, -32]], [[257, -207], [121, -285], [0, -218]]).map(map);
  d.step('伸展的果枝', '从右上方垂下一根弯枝，再分出通向花与小果的两条支枝；中间的空处留给成熟果。');
  d.stroke(curve([829, 82], [[684, 132], [716, 239], [652, 366]]), c[3], 20, 'ink');
  d.stroke(curve([820, 84], [[691, 147], [705, 238], [648, 353]]), t.accent, 6, 'ink');
  d.stroke(curve([715, 211], [[514, 127], [329, 172], [290, 311]]), c[3], 12, 'ink');
  d.stroke(curve([664, 284], [[490, 326], [303, 365], [242, 531]]), c[3], 12, 'ink');
  d.step('三出复叶', '叶片分成三片，边缘有轻微齿形；右上深、左上浅，用同一根叶脉说明朝向。');
  const leaf = (x: number, y: number, dx: number, dy: number, color: string) => {
    const len = Math.hypot(dx, dy), nx = -dy / len, ny = dx / len, p: XY[] = [[x, y]];
    for (let side = 1; side >= -1; side -= 2) for (let n = 1; n <= 18; n++) {
      const a = side === 1 ? n / 18 : 1 - n / 18, bulge = Math.sin(Math.PI * a) * len * 0.27 * (n % 2 ? 1 : 0.88) * side;
      p.push([x + dx * a + nx * bulge, y + dy * a + ny * bulge]);
    }
    paint(p, color);
    d.stroke([[x, y], [x + dx * 0.94, y + dy * 0.94]], t.accent, 5, 'ink');
    for (const a of [0.26, 0.46, 0.65]) for (const side of [-1, 1]) {
      const w = Math.sin(Math.PI * (a + 0.13)) * len * 0.21 * side;
      d.stroke([[x + dx * a, y + dy * a], [x + dx * (a + 0.16) + nx * w, y + dy * (a + 0.16) + ny * w]], t.accent, 3, 'ink', 0.7);
    }
  };
  leaf(549, 211, -224, -108, c[3]); leaf(549, 211, -183, 75, c[3]); leaf(549, 211, -10, -153, t.accent);
  leaf(728, 278, 162, -119, leafDark); leaf(728, 278, 181, 57, c[3]); leaf(728, 278, 65, 152, leafDark);
  d.step('还未成熟的小果', '小果在左后方，颜色偏奶黄、果尖才泛红；它比成熟果小很多。');
  paint(curve([284, 319], [[209, 283], [189, 363], [228, 426]], [[253, 480], [298, 510], [317, 486]], [[372, 419], [368, 303], [284, 319]]), c[2]);
  d.stroke(curve([319, 371], [[343, 407], [323, 461], [293, 480]]), c[1], 38, 'translucent', 0.48);
  paint([[285, 322], [230, 292], [250, 331], [230, 353], [281, 340], [314, 369], [315, 331], [342, 307], [306, 314], [282, 283]], c[3]);
  [[249, 372], [290, 373], [327, 375], [261, 411], [302, 416], [284, 455]].forEach(([x, y]) => d.stroke([[x, y], [x + 2, y + 15]], t.shade, 4, 'ink', 0.7));
  d.step('成熟果的体积', '大果的肩部饱满、底部收尖；先铺完整红色，再沿右下弧度压暗，亮面只放左上。');
  paint(berry, c[1], t.shade);
  d.stroke(curve(map([164, -133]), [map([194, 20]), map([113, 190]), map([9, 254])]), t.shade, 63, 'translucent', 0.64);
  d.stroke(curve(map([-118, -146]), [map([-177, -80]), map([-136, 53]), map([-84, 139])]), t.light, 52, 'translucent', 0.5);
  d.stroke(curve(map([-99, -145]), [map([-136, -103]), map([-126, -42]), map([-102, 9])]), t.light, 34, 'crayon', 0.22);
  d.step('萼片拥住果肩', '萼片放射着贴在红果上；中间两片翻折向下，果柄从它们后面接进来。');
  const calyx: [XY, XY, XY, XY][] = [
    [[0, -213], [-99, -283], [-170, -233], [-214, -192]],
    [[0, -213], [-50, -300], [4, -298], [4, -331]],
    [[0, -213], [64, -295], [148, -262], [196, -265]],
    [[0, -213], [94, -243], [146, -176], [191, -166]],
    [[0, -213], [74, -179], [50, -120], [52, -111]],
    [[0, -213], [-35, -175], [-90, -149], [-103, -126]],
  ];
  calyx.forEach(([start, a, b, end], i) => {
    const p = curve(start, [a, b, end], [[b[0] * 0.65, b[1] + 27], [a[0] * 0.24, a[1] + 41], start]).map(map);
    paint(p, i % 2 ? t.accent : c[3]);
    d.stroke([map(start), map([end[0] * 0.82, end[1] * 0.82 - 38])], leafDark, 4, 'ink', 0.55);
  });
  d.step('白花与金色花蕊', '白花与红果相呼应，五片花瓣先后交叠；金色花心压在花瓣接缝上。');
  const fx = 235, fy = 552;
  for (let i = 0; i < 5; i++) {
    const a = i * TAU / 5 - Math.PI / 2;
    paint(ellipse(0, -50, 36, 54).map(([u, v]): XY => [fx + u * Math.cos(a) - v * Math.sin(a), fy + u * Math.sin(a) + v * Math.cos(a)]), c[5]);
  }
  paint(ellipse(fx, fy, 29, 28), c[2]);
  for (let i = 0; i < 10; i++) { const a = i * TAU / 10; d.stroke([[fx + Math.cos(a) * 28, fy + Math.sin(a) * 27], [fx + Math.cos(a) * 38, fy + Math.sin(a) * 37]], t.shade, 4, 'ink'); }
  d.step('籽窝与果皮反光', '小籽长在表皮，按果面转弯、往尖部收拢；每颗先画暗窝再提金色，最后留两段湿润反光。');
  [6, 6, 5, 4, 3, 2].forEach((count, row) => {
    const v = -132 + row * 66, half = [139, 164, 155, 124, 85, 41][row];
    for (let i = 0; i < count; i++) {
      const u = (i / (count - 1) * 2 - 1) * half + (row % 2 ? 6 : 0);
      d.stroke([map([u, v]), map([u - u * 0.04, v + 22])], t.shade, 13, 'ink', 0.85);
      d.stroke([map([u - 2, v - 1]), map([u - u * 0.04 - 2, v + 18])], c[2], 5.5, 'ink');
    }
  });
  d.stroke(curve(map([-110, -158]), [map([-135, -138]), map([-145, -113]), map([-145, -82])]), c[5], 11, 'glow', 0.75);
  d.stroke(curve(map([-131, -38]), [map([-127, -12]), map([-120, 6]), map([-111, 25])]), c[5], 6, 'ink', 0.8);
};

// ───────────────────────────── 冰淇淋 ─────────────────────────────

/** 进阶：三球甜筒立着放在餐巾上，球体错开叠放，格纹蛋筒是教学重点。 */
const icecreamAdvanced: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  d.step('桌面和餐巾', '先铺桌面，再把餐巾画成一块带折角的浅色，甜筒才有落点。');
  band(d, 240, t.light, 84, 0.2, 26);
  band(d, 852, c[2], 104, 0.42, 24);
  d.shape([[214, 786], [812, 786], [866, 902], [152, 902]], c[5], c[5]);
  d.stroke(curve([244, 816], [[400, 796], [640, 806], [792, 826]]), c[3], 12, 'ink', 0.7);
  d.stroke([[560, 792], [610, 900]], c[3], 8, 'ink', 0.5);
  d.step('蛋筒和格纹', '蛋筒是个倒三角，口比尖宽一倍；格纹两组斜线交叉，线条只走到边缘里面。');
  d.shape([[382, 556], [634, 556], [508, 892]], c[2]);
  [[416, 612, 552, 792], [452, 664, 536, 826], [490, 716, 522, 856], [572, 612, 462, 792], [538, 664, 478, 826], [502, 716, 486, 856]]
    .forEach(([x, y, a, b]) => d.stroke([[x, y], [a, b]], c[0], 4, 'ink', 0.55));
  d.step('三球堆叠', '三颗球颜色不同、高度不同：左右两颗先放，最上面那颗压住它们的接缝。');
  d.shape(ellipse(430, 466, 122, 118), c[1]);
  d.shape(ellipse(596, 448, 112, 110), c[4]);
  d.shape(ellipse(512, 316, 118, 114), c[5]);
  d.step('球面的旋涡', '每一球都有自己的一圈旋涡线，顺着体积转，不是三条平行线。');
  d.stroke(ellipse(430, 466, 86, 78, -1.2, 1.6), c[5], 20, 'translucent', 0.5);
  d.stroke(ellipse(596, 448, 78, 72, -1.0, 1.7), c[5], 18, 'translucent', 0.42);
  d.stroke(ellipse(512, 316, 80, 76, -1.3, 1.5), t.light, 22, 'translucent', 0.5);
  d.stroke(curve([430, 424], [[476, 404], [520, 428], [536, 462]]), c[0], 5, 'ink', 0.5);
  d.step('樱桃和糖针', '顶上点一颗樱桃，糖针疏疏落落撒几根，最后压影子。');
  d.shape(ellipse(516, 194, 42, 42), c[1], c[0]);
  d.stroke(curve([518, 156], [[516, 118], [548, 100], [566, 112]]), c[3], 9, 'ink');
  d.dot(504, 182, 12, c[5]);
  [[356, 384], [672, 372], [430, 566], [612, 540], [522, 240]].forEach(([x, y], i) => d.stroke([[x, y], [x + 16, y - 18]], i % 2 ? c[3] : c[4], 8, 'ink'));
  softShadow(d, 512, 906, 260, c[0], 0.22);
};

/** 艺术：融化中的一杯冰淇淋。玻璃杯、流下的奶油、一把小勺，冷底配一处暖光。 */
const icecreamArt: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  d.drawing.background = c[0];
  d.step('暗底和上方的光', '艺术版把环境压成暗紫：上半部扫一层冷光，桌面用一整块浅色托住杯子。');
  washField(d, c[4], 0.22, -40, 520, 132, 176);
  d.stroke(curve([-30, 250], [[320, 200], [700, 320], [1030, 250]]), c[5], 148, 'crayon', 0.14, true);
  d.fill([[0, 848], [1000, 848], [1000, 1010], [0, 1010]], c[4]);
  d.stroke([[0, 848], [1000, 848]], c[5], 10, 'glow', 0.5);
  d.fill([[0, 944], [1000, 944], [1000, 1010], [0, 1010]], c[2]);
  d.step('玻璃杯', '杯子先画杯口那个椭圆，再往下收成碗，最后接一段杯脚——玻璃要透、要浅。');
  d.shape(curve([288, 512], [[296, 690], [400, 774], [508, 776]], [[620, 774], [722, 688], [730, 512]], [[620, 452], [398, 452], [288, 512]]), c[5]);
  d.stroke(curve([296, 620], [[340, 736], [420, 786], [508, 790]]), c[4], 76, 'translucent', 0.42);
  d.stroke(curve([508, 316], [[640, 330], [728, 420], [736, 540]]), c[4], 66, 'translucent', 0.3);
  d.shape(ellipse(508, 500, 222, 58), c[5]);
  d.shape([[478, 776], [540, 776], [540, 872], [478, 872]], c[5]);
  d.shape(ellipse(508, 888, 118, 30), c[5]);
  d.step('融化的三球', '球体边缘全部往下淌：每一滴都从球体最鼓的地方出发，长短不一样。');
  d.shape(ellipse(404, 440, 122, 112), c[1]);
  d.shape(ellipse(614, 428, 116, 108), c[4]);
  d.shape(ellipse(510, 314, 126, 118), c[5]);
  [[352, 512, 54], [420, 528, 86], [472, 522, 40], [552, 520, 74], [622, 512, 48], [676, 500, 62]]
    .forEach(([x, y, len], i) => d.stroke(curve([x, y], [[x - 8, y + len * 0.5], [x + 10, y + len * 0.8], [x, y + len]]), i % 2 ? c[5] : c[1], 24 - i * 2, 'ink', 0.9));
  d.step('奶油和糖针', '奶油只提一圈旋涡和几道高光；糖针撒在融化处附近，别铺满整杯。');
  d.stroke(curve([404, 384], [[452, 350], [520, 356], [560, 392]]), t.light, 42, 'crayon', 0.45);
  d.stroke(curve([560, 372], [[612, 350], [660, 366], [690, 396]]), c[5], 26, 'translucent', 0.55);
  [[366, 404], [574, 352], [658, 470], [470, 268], [404, 522]].forEach(([x, y], i) => d.stroke([[x, y], [x + 15, y - 17]], i % 2 ? c[3] : c[1], 8, 'ink'));
  d.step('插进去的小勺', '小勺插在右上方，柄露在杯外；勺子挡住的那一部分球体要先画。');
  d.shape([[672, 372], [712, 366], [744, 560], [710, 566]], c[2]);
  d.shape(ellipse(700, 340, 46, 62), c[2], c[0]);
  d.stroke([[692, 330], [706, 302]], c[0], 7, 'ink');
  d.step('玻璃的反光', '玻璃要靠三样东西：一条竖直高光、杯口那圈亮线、还有杯底压深的边。');
  d.stroke(curve([320, 540], [[332, 656], [382, 738], [440, 764]]), c[5], 18, 'glow', 0.6);
  d.stroke([[430, 520], [446, 740]], c[5], 13, 'glow', 0.5);
  d.stroke(ellipse(508, 500, 218, 54, 0.2, 2.9), c[5], 9, 'glow', 0.85);
  d.stroke(curve([300, 742], [[388, 812], [628, 812], [716, 740]]), c[4], 26, 'translucent', 0.3);
  d.step('桌面倒影', '桌面上补一块倒过来的浅影，最后在杯口点两处最亮的光。');
  d.stroke(curve([318, 930], [[420, 906], [600, 906], [700, 932]]), c[4], 52, 'crayon', 0.42, true);
  softShadow(d, 508, 948, 246, c[4], 0.34);
  d.stroke([[420, 306], [468, 292]], c[5], 11, 'glow', 0.95);
  d.stroke([[330, 430], [348, 404]], c[5], 9, 'glow', 0.8);
};

// ───────────────────────────── 小房子 ─────────────────────────────

/** 进阶：花园里带侧面的小屋，用完整地面、两面墙和斜屋顶教透视。 */
const houseAdvanced: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  // 每个色面都由可回放的长排线铺满，不沿用远山条带或自动暗部。
  const area = (points: XY[], color: string, edge?: string) => {
    d.fill(points, color);
    if (edge) d.stroke([...points, points[0]], edge, 4.5, 'ink');
  };
  d.drawing.background = t.paper;
  d.step('一片相连的花园', '天空留白，草地从远处的弧形地平线一直铺到画底；小路先画，尽头留给门口。');
  cloud(d, 206, 190, 58, '#F2F7FF', c[4], 0.12);
  cloud(d, 855, 146, 35, '#F2F7FF', c[4], 0.1);
  area(curve([-30, 668], [[250, 575], [712, 580], [1030, 635]], [[1040, 840], [1030, 945], [1030, 1030]], [[630, 1040], [250, 1040], [-30, 1030]], [[-30, 915], [-30, 760], [-30, 668]]), '#D6E8B0');
  area(curve([352, 711], [[354, 828], [310, 870], [298, 1030]], [[376, 1040], [474, 1040], [555, 1030]], [[486, 873], [411, 812], [428, 721]]), c[5]);
  softShadow(d, 624, 754, 214, c[3], 0.15);
  d.step('亮墙和侧墙', '先画朝向我们的五边形墙，再接右边的侧墙；侧墙用沙色，墙角共用同一条竖线。');
  area([[564, 449], [800, 361], [800, 650], [564, 744]], '#D9C0A3', t.shade);
  area([[290, 445], [425, 312], [566, 450], [566, 744], [290, 716]], c[5], t.shade);
  d.stroke([[570, 476], [570, 732]], t.shade, 13, 'translucent', 0.22);
  d.stroke([[304, 713], [566, 740], [789, 652]], t.shade, 13, 'ink', 0.75);
  d.step('斜屋顶与烟囱', '屋脊向右上方退去，瓦面顺着坡度排线；烟囱从后排瓦片里长出来，屋檐盖住墙的上缘。');
  area([[425, 294], [650, 210], [839, 367], [582, 472]], c[1], '#8C3B2E');
  area([[425, 294], [650, 210], [680, 238], [452, 323]], '#E0A458');
  for (let i = 1; i <= 4; i++) {
    const u = i / 5;
    d.stroke([[425 + 225 * u, 294 - 84 * u], [582 + 257 * u, 472 - 105 * u]], '#8C3B2E', 4.5, 'ink', 0.6);
  }
  d.stroke([[480, 355], [713, 267]], t.light, 6, 'crayon', 0.6);
  d.stroke([[531, 411], [778, 317]], '#8C3B2E', 4, 'ink', 0.7);
  area([[686, 215], [725, 204], [725, 289], [686, 302]], '#E0A458', '#8C3B2E');
  area([[725, 204], [750, 216], [750, 280], [725, 289]], '#8C3B2E');
  d.stroke([[682, 214], [725, 200], [753, 213]], c[5], 12, 'ink');
  d.stroke([[264, 451], [425, 290], [588, 467], [843, 369]], '#8C3B2E', 16, 'ink');
  d.stroke([[276, 452], [425, 304], [580, 472]], t.light, 6, 'ink');
  d.stroke(curve([718, 178], [[692, 150], [748, 124], [730, 93]]), '#F2F7FF', 24, 'crayon', 0.7);
  d.step('门窗跟着墙面转', '正门和阁楼窗画在亮墙上；侧窗的上下边随墙向右抬高，窗框与花箱也跟着这个方向。');
  const door = curve([347, 721], [[347, 674], [347, 631], [347, 584]], [[347, 536], [423, 545], [425, 591]], [[425, 631], [425, 681], [425, 730]]);
  area(door, c[3], c[0]);
  d.stroke([[363, 599], [408, 604], [408, 654], [363, 649], [363, 599]], '#D6E8B0', 4, 'ink');
  d.stroke([[362, 667], [408, 672]], t.accent, 5, 'ink');
  d.dot(410, 670, 10, t.light);
  d.ring(430, 431, 34, 37, t.shade, 8);
  d.fill(ellipse(430, 431, 28, 31), c[4]);
  d.stroke([[402, 428], [458, 433]], c[5], 5, 'ink');
  d.stroke([[432, 403], [430, 461]], c[5], 5, 'ink');
  const window = [[622, 506], [742, 461], [742, 564], [622, 609]] as XY[];
  area(window, c[4], c[5]);
  d.stroke([[629, 513], [735, 473]], '#F2F7FF', 9, 'translucent', 0.85);
  d.stroke([[682, 486], [682, 586]], c[5], 7, 'ink');
  d.stroke([[625, 554], [738, 512]], c[5], 7, 'ink');
  area([[614, 610], [751, 560], [746, 585], [620, 633]], c[1], '#8C3B2E');
  d.stroke([[628, 602], [738, 561]], c[3], 16, 'ink');
  [642, 679, 717].forEach((x, i) => d.stroke([[x, 586 - i * 14], [x + 3, 566 - i * 14]], '#E24B6B', 15, 'ink'));
  d.step('花园的前后层次', '右边的矮树丛压住墙脚，左边只留几株高花；草叶从同一个根部抽出，不把细节撒满天空。');
  const bush = curve([754, 744], [[732, 712], [754, 678], [782, 680]], [[776, 637], [836, 613], [852, 661]], [[889, 641], [924, 686], [902, 721]], [[870, 744], [802, 758], [754, 744]]);
  area(bush, c[3]);
  d.stroke(curve([778, 706], [[806, 689], [824, 683], [843, 676]]), t.accent, 18, 'crayon', 0.6);
  [[165, 799, 110], [214, 816, 72], [827, 881, 88]].forEach(([x, y, h]) => {
    d.stroke(curve([x, y], [[x - 7, y - h / 2], [x + 7, y - h * 0.7], [x, y - h]]), c[3], 5, 'ink');
    d.leaf(x, y - 28, -32, -28); d.leaf(x, y - 44, 32, -27);
    d.stroke([[x - 9, y - h], [x + 9, y - h]], c[1], 21, 'ink');
    d.dot(x, y - h, 8, t.light);
  });
  grass(d, 249, 929, 42, c[3]); grass(d, 666, 859, 32, c[3]);
  d.stroke(curve([338, 855], [[345, 884], [361, 915], [381, 939]]), t.shade, 11, 'crayon', 0.16);
};

/** 艺术：暮色剪影。蓝调天空压一层暖色地平线，房子、山和树全是剪影，只有窗里点着灯。 */
const houseArt: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  d.drawing.background = c[4];
  d.step('暮色天空', '蓝调从上往下压，接近地平线的地方留两层暖色——上淡下亮，光从下面来。');
  washField(d, K, 0.3, -40, 300, 108, 152);
  washField(d, c[4], 0.3, 240, 560, 104, 148);
  d.fill([[0, 470], [1000, 470], [1000, 610], [0, 610]], c[1]);
  d.fill([[0, 586], [1000, 586], [1000, 660], [0, 660]], c[2]);
  d.step('月亮和星', '一轮满月挂在右上，光晕只围一圈；星星疏疏几颗，别撒满。');
  d.shape(ellipse(802, 168, 62, 60), c[5], c[5]);
  d.stroke(ellipse(802, 168, 90, 86, 3.4, 6.0), c[5], 22, 'glow', 0.4);
  [[140, 130], [280, 214], [420, 96], [560, 192], [936, 306]].forEach(([x, y], i) => d.stroke([[x, y], [x, y + 22 + i * 2]], c[5], 7 - i % 3, 'glow', 0.8));
  d.step('远山和地面', '远山一层灰绿剪影贴住暖色，地面一整块暗色压到画底。');
  d.fill(curve([-20, 640], [[260, 566], [540, 656], [820, 580]], [[1024, 620], [1024, 780], [-20, 780]]), c[3]);
  d.fill(curve([-20, 760], [[300, 718], [640, 780], [1020, 728]], [[1020, 1040], [-20, 1040], [-20, 760]]), K);
  d.stroke(curve([-20, 760], [[300, 718], [640, 780], [1020, 728]]), c[2], 12, 'translucent', 0.3);
  d.step('小屋剪影', '屋顶要比墙宽出一截，烟囱并进同一个暗块，剪影才认得出是房子。');
  d.shape([[604, 540], [648, 540], [648, 640], [604, 640]], K, K);
  d.shape([[400, 622], [650, 622], [650, 764], [400, 764]], K, K);
  d.shape([[352, 632], [524, 496], [696, 632]], K, K);
  d.stroke(curve([642, 530], [[618, 480], [672, 448], [656, 398]]), c[5], 28, 'crayon', 0.4);
  d.step('窗里的灯', '灯是全画唯一的暖色：两扇窗一道门缝，光只往窗下的地上洒一点。');
  d.shape([[428, 654], [478, 654], [478, 700], [428, 700]], c[2], c[2]);
  d.shape([[502, 654], [552, 654], [552, 700], [502, 700]], t.light, t.light);
  d.shape([[576, 654], [622, 654], [622, 764], [576, 764]], c[2], c[2]);
  [[453, 700], [527, 700], [599, 764]].forEach(([x, y]) => d.stroke([[x, y], [x, y + 56]], c[2], 30, 'crayon', 0.3, true));
  d.step('松树和老树', '左边三棵高矮错开的松树，右边一棵大树从画外伸进来收住画幅。');
  [96, 190, 292].forEach((x, i) => {
    const h = 150 - i * 22;
    d.shape([[x - 44, 760], [x, 760 - h], [x + 44, 760]], K, K);
    d.shape([[x - 34, 702], [x, 702 - h * 0.72], [x + 34, 702]], K, K);
  });
  d.stroke([[950, 1040], [930, 640]], K, 38, 'ink');
  d.stroke([[932, 720], [836, 656]], K, 17, 'ink');
  d.shape(ellipse(948, 520, 150, 128), K, K);
  d.shape(ellipse(852, 566, 92, 76), K, K);
  d.step('小路和收边', '门口一条被灯光照亮的小路弯到画外，再补两丛草，画面就收在这里。');
  d.stroke(curve([599, 764], [[580, 830], [520, 900], [452, 986]]), c[2], 48, 'translucent', 0.4);
  d.stroke(curve([610, 770], [[596, 838], [544, 906], [486, 980]]), t.light, 22, 'translucent', 0.3);
  grass(d, 150, 1010, 70, c[0]); grass(d, 330, 1016, 56, c[0]);
};

// ───────────────────────────── 帆船 ─────────────────────────────

/** 进阶：白昼航行。船放大到画面中央偏左，两面帆分开，浪和泡沫教运笔方向。 */
const boatAdvanced: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  d.step('天空和太阳', '天空上淡下亮，一朵云一个太阳；先铺天和海，船才有位置。');
  washField(d, c[3], 0.22, -40, 340, 108, 150);
  washField(d, c[5], 0.5, 300, 430, 90, 140);
  cloud(d, 210, 150, 56, c[5], c[3], 0.3);
  d.shape(ellipse(806, 168, 78, 78), c[2]);
  d.stroke(ellipse(806, 168, 110, 110, 2.6, 5.2), c[2], 28, 'glow', 0.4);
  d.step('海平线和远海', '海平线是一条笔直的暗线；远海颜色浅、波纹短，越近越深越长。');
  d.stroke([[30, 396], [972, 392]], c[4], 11, 'ink');
  washField(d, c[3], 0.46, 402, 1050, 118, 160);
  washField(d, c[4], 0.34, 670, 1060, 128, 172);
  for (let i = 0; i < 5; i++) d.stroke(curve([84, 452 + i * 44], [[330, 440 + i * 42], [672, 468 + i * 40], [930, 446 + i * 44]]), c[5], 7, 'crayon', 0.4, true);
  d.step('船身和桅杆', '船身两头翘、底边平；桅杆竖直立在船身正中，先定高度再挂帆。');
  d.shape(curve([300, 606], [[460, 654], [560, 652], [706, 600]], [[664, 698], [536, 728], [408, 710]], [[338, 672], [310, 626], [300, 606]]), c[1]);
  d.stroke([[504, 600], [504, 152]], c[0], 15, 'ink');
  d.stroke(curve([316, 642], [[462, 684], [596, 682], [696, 634]]), c[0], 12, 'ink', 0.55);
  d.step('两面船帆', '主帆在右、前帆在左，两片形状不一样；顶上再加一面小旗指风向。');
  d.shape([[486, 182], [486, 572], [716, 572]], c[5]);
  d.shape([[524, 240], [524, 578], [318, 578]], c[2]);
  d.shape([[506, 150], [602, 176], [506, 212]], c[1]);
  d.stroke([[486, 186], [486, 568]], c[0], 6, 'ink', 0.6);
  d.step('浪和泡沫', '近处的浪是几组同方向的弧；浪尖上留白当泡沫，最后补三只海鸥定尺度。');
  for (let i = 0; i < 4; i++) d.stroke(curve([36 + i * 16, 780 + i * 58], [[300, 736 + i * 56], [676, 826 + i * 52], [968 - i * 14, 764 + i * 58]]), c[3], 30, 'ink', 0.8);
  for (let i = 0; i < 4; i++) d.stroke(curve([128 + i * 22, 764 + i * 58], [[300, 728 + i * 56], [520, 778 + i * 54], [708, 752 + i * 56]]), c[5], 9, 'ink', 0.85);
  [[176, 288, 44], [262, 238, 38], [742, 320, 48]].forEach(([x, y, r]) => d.stroke(curve([x - r, y], [[x - r * 0.4, y - r * 0.5], [x - r * 0.1, y - r * 0.42], [x, y]], [[x + r * 0.12, y - r * 0.44], [x + r * 0.5, y - r * 0.52], [x + r, y]]), c[0], 7, 'ink'));
  softShadow(d, 508, 748, 190, c[4], 0.3);
};

/** 艺术：落日归帆。船只剩剪影，太阳干净地悬在海平线上，光路一路铺到前景。 */
const boatArt: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  d.drawing.background = c[1];
  d.step('晚霞渐变', '整幅先交给晚霞：顶部压深，靠海平线一层层加亮。');
  washField(d, c[0], 0.4, -40, 220, 104, 152);
  washField(d, c[1], 0.5, 180, 420, 100, 148);
  washField(d, c[2], 0.36, 360, 500, 88, 138);
  d.step('落日', '太阳是一轮干净的圆，下缘贴着海平线；光晕只围一圈，别叠太多层。');
  d.shape(ellipse(660, 400, 128, 128), c[2]);
  d.stroke(ellipse(660, 400, 168, 164, 3.2, 6.1), c[5], 30, 'glow', 0.45);
  d.step('海平线和海面', '海平线一道深色直线；海面深蓝打底，越靠近前景越暗。');
  d.stroke([[20, 492], [980, 488]], c[0], 13, 'ink');
  washField(d, c[4], 0.55, 498, 1050, 116, 158);
  washField(d, c[3], 0.3, 560, 900, 118, 156);
  washField(d, c[0], 0.4, 720, 1060, 124, 168);
  d.step('船影', '船身、桅杆、两面帆合成一个暗块，落在太阳左边；剪影不画细节，只求形状认得出来。');
  d.shape(curve([250, 600], [[408, 648], [540, 646], [690, 592]], [[652, 682], [506, 712], [372, 694]], [[290, 660], [262, 618], [250, 600]]), K, K);
  d.stroke([[472, 594], [472, 168]], K, 17, 'ink');
  d.shape([[450, 198], [450, 564], [680, 564]], K, K);
  d.shape([[492, 262], [492, 572], [288, 572]], K, K);
  d.step('海面的光路', '太阳底下是一条竖直的光路：越近的横笔越长、越疏，一直铺到画面底边。');
  for (let i = 0; i < 11; i++) {
    const y = 520 + i * 44, half = 40 + i * 24;
    d.stroke(curve([660 - half, y], [[660 - half * 0.3, y - 8], [660 + half * 0.3, y - 8], [660 + half, y]]), i % 3 ? c[2] : t.light, 9 + i * 1.6, 'translucent', 0.5 - i * 0.02, true);
  }
  d.step('逆光的边缘', '只在朝太阳的那一边描一条暖线：桅杆右沿、帆的外边、船头。');
  d.stroke([[478, 180], [478, 588]], c[2], 8, 'glow', 0.6);
  d.stroke([[680, 558], [456, 558]], c[2], 8, 'glow', 0.5);
  d.stroke(curve([292, 572], [[380, 578], [452, 580], [492, 572]]), c[2], 7, 'glow', 0.5);
  d.step('远帆、海鸥和收边', '远处一只小帆把海面的尺度撑开，三只海鸥落在太阳边，前景再压一层深浪。');
  d.stroke([[176, 546], [176, 488]], K, 5, 'ink');
  d.shape([[168, 494], [168, 542], [126, 542]], K, K);
  d.shape([[184, 502], [184, 542], [220, 542]], K, K);
  d.stroke([[118, 546], [232, 544]], K, 8, 'ink');
  [[820, 260], [884, 300], [756, 210]].forEach(([x, y], i) => d.stroke(curve([x - 38 + i * 5, y], [[x - 15, y - 19], [x - 4, y - 16], [x, y]], [[x + 5, y - 17], [x + 17, y - 20], [x + 40 - i * 5, y]]), c[0], 6, 'ink'));
  for (let i = 0; i < 4; i++) d.stroke(curve([60 + i * 24, 886 + i * 40], [[320, 856 + i * 38], [660, 930 + i * 34], [940, 880 + i * 40]]), c[0], 16 + i * 4, 'ink', 0.75);
};

// ───────────────────────────── 热气球 ─────────────────────────────

/** 进阶：近景热气球。球面用真实经线分瓣，吊篮、绳索和远山交代高度。 */
const balloonAdvanced: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  const cx = 430, top = 118, r = 202, mouth = 118 + 2.06 * 202;
  d.step('天空和云', '天空是一层淡紫，云放在气球两侧：左边大、右边小，中间留给主体。');
  washField(d, c[4], 0.18, -40, 900, 128, 170);
  cloud(d, 176, 372, 78, c[5], c[4], 0.3);
  cloud(d, 806, 262, 52, c[5], c[4], 0.26);
  d.step('气球的分瓣', '球面先想成一颗球：每条经线都从顶点出发、绕到气口，瓣与瓣共边。');
  const seam = [1, 0.809, 0.309, -0.309, -0.809, -1];
  [c[1], c[2], c[4], c[3], c[0]].forEach((color, i) => d.shape(gore(cx, top, r, seam[i], seam[i + 1], mouth), color));
  d.line(meridian(cx, top, r, 1, mouth), c[0], 9, true);
  d.line(meridian(cx, top, r, -1, mouth).slice().reverse(), c[0], 9, true);
  d.stroke(ellipse(cx, mouth, r * 0.2, r * 0.05), c[0], 8, 'ink');
  d.step('吊篮和绳索', '四根绳子从气口分到篮角，篮子比气口宽一点，才有吊着的感觉。');
  [[cx - 40, mouth, cx - 78, 772], [cx - 14, mouth + 6, cx - 30, 772], [cx + 14, mouth + 6, cx + 30, 772], [cx + 40, mouth, cx + 78, 772]]
    .forEach(([x, y, a, b]) => d.stroke([[x, y], [a, b]], c[0], 6, 'ink'));
  d.shape([[cx - 86, 766], [cx + 86, 766], [cx + 70, 880], [cx - 70, 880]], c[2]);
  d.stroke([[cx - 74, 792], [cx + 74, 792]], c[0], 7, 'ink');
  d.stroke([[cx - 70, 836], [cx + 70, 836]], c[0], 7, 'ink');
  d.step('布面的光', '光从左上打过来：左侧两瓣提亮，右侧两瓣压深，接缝再用毛笔顺一遍。');
  d.stroke(meridian(cx, top, r, 0.62, mouth), c[5], 26, 'translucent', 0.32);
  d.stroke(meridian(cx, top, r, -0.62, mouth), c[0], 30, 'translucent', 0.24);
  d.stroke(meridian(cx, top, r, -0.93, mouth), c[0], 22, 'translucent', 0.2);
  d.stroke(meridian(cx, top, r, 0.86, mouth), c[5], 12, 'glow', 0.6);
  d.step('远山和飞鸟', '底部补一条远山和几丛草，再放一只更小的气球，高度和尺度就出来了。');
  d.shape(curve([-20, 962], [[240, 876], [430, 940], [620, 872]], [[840, 802], [984, 890], [1024, 902]], [[520, 1010], [220, 1010], [-20, 962]]), c[3]);
  d.shape(curve([-20, 1010], [[260, 946], [470, 998], [680, 944]], [[880, 890], [990, 962], [1024, 972]], [[520, 1040], [220, 1040], [-20, 1010]]), c[0]);
  d.stroke(ellipse(848, 512, 34, 44), c[1], 24, 'ink');
  d.stroke([[848, 556], [848, 610]], c[0], 4, 'ink');
  d.shape([[836, 608], [860, 608], [856, 630], [840, 630]], c[0]);
  [[708, 200, 40], [768, 156, 32]].forEach(([x, y, w]) => d.stroke(curve([x - w, y], [[x - w * 0.35, y - 18], [x - w * 0.1, y - 15], [x, y]], [[x + w * 0.12, y - 17], [x + w * 0.4, y - 19], [x + w, y]]), c[0], 6, 'ink'));
};

/** 艺术：暮色远行。气球正好压在落日上，剪影被光晕托住，云海在脚下。 */
const balloonArt: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  const cx = 588, top = 318, r = 148, mouth = 318 + 2.06 * 148;
  d.drawing.background = c[0];
  d.step('暮色天空', '艺术版从紫黑走向橘黄：上半压暗、靠近地平线一层层加暖，光才有来处。');
  washField(d, c[4], 0.22, -40, 300, 118, 168);
  washField(d, c[4], 0.3, 280, 520, 108, 160);
  washField(d, c[1], 0.48, 500, 830, 96, 152);
  d.step('落日', '落日压在气球后面，是整幅最亮的一块；气球站在它前面才有逆光。');
  d.shape(ellipse(596, 606, 206, 194), c[2]);
  d.stroke(ellipse(596, 606, 250, 236, 3.3, 6.1), c[2], 52, 'glow', 0.5);
  d.step('云海', '脚下的云海分成三层，越远越淡；云不要画满，落日的下缘要留出来。');
  washField(d, c[4], 0.34, 848, 1060, 92, 152);
  d.stroke(curve([-20, 866], [[240, 796], [520, 862], [790, 794]], [[930, 758], [998, 826], [1024, 846]]), c[4], 58, 'crayon', 0.45, true);
  d.stroke(curve([-20, 952], [[260, 892], [540, 956], [820, 890]], [[948, 854], [1002, 912], [1024, 930]]), c[5], 52, 'crayon', 0.3, true);
  d.step('气球剪影', '气球整块压成暗色、压住落日：剪影越整，轮廓越好认。');
  const seam = [1, 0.809, 0.309, -0.309, -0.809, -1];
  [1, 2, 3, 4, 5].forEach(i => d.shape(gore(cx, top, r, seam[i - 1], seam[i], mouth), c[0]));
  d.line(meridian(cx, top, r, 1, mouth), c[0], 8, true);
  d.line(meridian(cx, top, r, -1, mouth).slice().reverse(), c[0], 8, true);
  d.step('球里透出的光', '烧嘴把球里照亮：气口上方一小片暖光，朝着球心淡出，别涂满整只球。');
  d.stroke(ellipse(cx, mouth - 40, r * 0.5, r * 0.34), c[2], 62, 'glow', 0.65);
  d.stroke(ellipse(cx, mouth - 22, r * 0.32, r * 0.2), c[5], 34, 'glow', 0.75);
  d.stroke([[cx, mouth + 6], [cx, mouth + 48]], c[2], 17, 'glow', 0.95);
  d.stroke([[cx - 38, mouth - 58], [cx + 38, mouth - 58]], c[0], 6, 'ink', 0.5);
  d.step('稀疏的星', '天上只留五颗星，越靠上越亮；星星是画面里最安静的部分。');
  [[120, 120], [268, 206], [906, 132], [812, 316], [346, 86]].forEach(([x, y], i) => d.stroke([[x, y], [x, y + 28 + i * 3]], c[5], 8 - i, 'glow', 0.85));
  d.step('吊篮与收光', '篮子和绳索也是剪影，只在朝落日的一侧描一条暖边，把画面收住。');
  [[cx - 30, mouth, cx - 60, 732], [cx - 10, mouth + 6, cx - 24, 732], [cx + 10, mouth + 6, cx + 24, 732], [cx + 30, mouth, cx + 60, 732]]
    .forEach(([x, y, a, b]) => d.stroke([[x, y], [a, b]], c[0], 6, 'ink'));
  d.shape([[cx - 68, 728], [cx + 68, 728], [cx + 56, 826], [cx - 56, 826]], c[0]);
  d.stroke([[cx + 34, mouth + 24], [cx + 44, 560]], c[2], 10, 'glow', 0.55);
  d.stroke([[cx - 34, mouth + 24], [cx - 58, 690]], c[2], 7, 'glow', 0.4);
};

// ───────────────────────────── 小火箭 ─────────────────────────────

/** 进阶：穿出云层。改成刚离开云海的角度，机身在中间，尾焰和速度线负责动势。 */
const rocketAdvanced: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  d.step('高空和云海', '上面是稀薄的高空，下面是厚厚的云海：两层颜色分开，火箭才有“冲出去”的感觉。');
  washField(d, c[3], 0.34, -40, 420, 110, 152);
  washField(d, c[4], 0.16, -40, 240, 120, 168);
  for (let i = 0; i < 4; i++) cloud(d, 150 + i * 250, 842 + (i % 2) * 62, 86 - (i % 2) * 14, c[5], c[4], 0.3);
  d.step('机身和尾翼', '机身是上下对称的流线形：鼻锥一个尖，两片尾翼左右张开。');
  d.shape(curve([472, 118], [[386, 236], [398, 508], [420, 690]], [[470, 716], [540, 716], [562, 690]], [[584, 508], [596, 236], [472, 118]]), c[5]);
  d.shape(curve([472, 118], [[424, 172], [404, 236], [396, 288]], [[470, 320], [540, 320], [586, 288]], [[578, 236], [558, 172], [472, 118]]), c[1]);
  d.shape([[398, 596], [318, 726], [322, 806], [446, 706]], c[1]);
  d.shape([[584, 596], [664, 726], [660, 806], [536, 706]], c[1]);
  d.step('舷窗和舱门', '舷窗是机身唯一的圆，外面一圈金属环；下面再来一块方形舱门平衡画面。');
  d.shape(ellipse(490, 432, 76, 76), c[3]);
  d.shape(ellipse(490, 432, 54, 54), c[0]);
  d.stroke(curve([462, 424], [[464, 402], [482, 390], [502, 394]]), c[5], 12, 'ink');
  d.shape([[452, 566], [532, 566], [532, 640], [452, 640]], c[3]);
  d.stroke([[490, 568], [490, 638]], c[0], 5, 'ink');
  d.stroke([[468, 508], [528, 508]], c[0], 6, 'ink', 0.5);
  d.step('尾焰分层', '尾焰分三层：外层喷枪最散、中层蜡笔最亮、芯部光晕最白。');
  d.stroke(curve([494, 712], [[432, 806], [452, 892], [496, 972]]), c[2], 62, 'spray', 0.4);
  d.stroke(curve([494, 716], [[462, 800], [472, 872], [498, 940]]), c[2], 54, 'crayon', 0.85);
  d.stroke(curve([494, 722], [[482, 796], [490, 862], [498, 918]]), c[5], 26, 'glow', 0.9);
  d.step('速度线和星点', '速度线全部朝同一个方向、长短错开；星点只落在没有云的地方。');
  for (let i = 0; i < 7; i++) d.stroke([[96 + i * 118, 726 - i * 62], [152 + i * 118, 676 - i * 62]], c[3], 7, 'ink', 0.5);
  [[158, 196, 18], [286, 348, 12], [712, 178, 15], [842, 420, 11], [622, 96, 13]].forEach(([x, y, s]) => { d.stroke([[x - s, y], [x + s, y]], c[5], 7, 'glow', 0.7); d.stroke([[x, y - s], [x, y + s]], c[5], 7, 'glow', 0.7); });
};

/** 艺术：掠过蓝星的火箭。所有零件共用同一根斜轴，完整机身从行星弧线前飞出。 */
const rocketArt: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  d.drawing.background = t.paper;
  // u 是机身横向，v 是由鼻锥指向喷口的纵向；避免鼻锥、舷窗、尾翼各自倾斜。
  const place = (points: XY[]): XY[] => points.map(([u, v]) => [564 + u * 0.832 - v * 0.555, 426 + u * 0.555 + v * 0.832]);
  const solid = (points: XY[], color: string, width = 9) => { d.fill(points, color); d.stroke(points, color, width, 'pen', 1, true); };
  const panel = (points: XY[], color: string, edge = c[0]) => {
    const p = place(points); solid(p, color); d.stroke(p, edge, 4.5, 'ink');
  };
  d.step('深空与远星', '留出大块安静的深蓝，星星疏密错开；中间那条斜线留给飞行中的火箭。');
  [[164, 178, 17], [842, 340, 20], [270, 542, 13]].forEach(([x, y, r]) => {
    d.stroke([[x - r, y], [x + r, y]], c[5], 4, 'glow', 0.7);
    d.stroke([[x, y - r], [x, y + r]], c[5], 4, 'glow', 0.7);
  });
  [[92, 394], [280, 280], [414, 114], [880, 146], [904, 586], [754, 748], [594, 876], [104, 582], [340, 414], [808, 916]].forEach(([x, y], i) => d.dot(x, y, 4 + i % 3, c[5]));
  d.step('蓝星的弧面', '只露出左下角的一弯行星：海洋先成一个整面，陆地与云沿球面转弯，亮边贴着地平线。');
  const horizon = curve([-28, 792], [[138, 658], [364, 760], [410, 1030]]);
  d.fill([...horizon, [-28, 1030], [-28, 792]], '#0E5A78');
  solid(curve([-24, 826], [[42, 770], [64, 799], [86, 848]], [[112, 858], [178, 830], [182, 888]], [[160, 934], [96, 906], [90, 960]], [[34, 982], [-22, 934], [-24, 826]]), '#2E9CA8');
  solid(curve([222, 855], [[290, 849], [326, 916], [346, 1008]], [[294, 1030], [238, 990], [252, 950]], [[276, 898], [218, 910], [222, 855]]), '#2E9CA8');
  d.stroke(curve([12, 824], [[142, 764], [276, 840], [336, 944]]), c[5], 19, 'translucent', 0.3, true);
  d.stroke(curve([24, 920], [[94, 902], [168, 940], [190, 995]]), c[5], 11, 'ink', 0.4, true);
  d.stroke(horizon, c[3], 11, 'glow', 0.35, true);
  d.stroke(horizon, c[3], 3, 'ink', 0.8, true);
  d.step('尾翼与喷口', '先画会被船身挡住的两片尾翼和喷口，两边都沿同一根斜轴展开。');
  panel(curve([-82, 46], [[-134, 82], [-170, 152], [-166, 216]], [[-136, 207], [-86, 189], [-57, 157]], [[-61, 118], [-74, 81], [-82, 46]]), c[1]);
  panel(curve([82, 46], [[134, 82], [170, 152], [166, 216]], [[136, 207], [86, 189], [57, 157]], [[61, 118], [74, 81], [82, 46]]), c[1]);
  panel([[-45, 166], [45, 166], [39, 211], [-39, 211], [-45, 166]], t.shade);
  d.stroke(place([[-37, 202], [37, 202]]), c[0], 10, 'ink');
  d.stroke(place(curve([-99, 88], [[-125, 120], [-139, 152], [-143, 183]])), c[5], 7, 'translucent', 0.35);
  d.step('完整船身与鼻锥', '一口气连起尖鼻、鼓起的肩部与收紧的船尾，再把红鼻锥接在船身上，轮廓不要断开。');
  const hull = curve([0, -282], [[-86, -228], [-111, -144], [-99, 26]], [[-96, 99], [-84, 154], [-58, 180]], [[-23, 190], [23, 190], [58, 180]], [[84, 154], [96, 99], [99, 26]], [[111, -144], [86, -228], [0, -282]]);
  panel(hull, c[5]);
  panel(curve([0, -282], [[-54, -244], [-87, -194], [-97, -132]], [[-45, -118], [45, -118], [97, -132]], [[87, -194], [54, -244], [0, -282]]), c[1]);
  solid(place(curve([70, -116], [[96, -77], [92, 72], [54, 176]], [[32, 182], [23, 182], [15, 182]], [[55, 100], [75, -11], [70, -116]])), t.shade, 8);
  panel(curve([-82, 133], [[-41, 144], [41, 144], [82, 133]], [[76, 155], [66, 173], [58, 180]], [[23, 190], [-23, 190], [-58, 180]], [[-66, 173], [-76, 155], [-82, 133]]), c[1]);
  d.stroke(place(curve([-20, -250], [[-46, -223], [-62, -184], [-67, -153]])), c[5], 11, 'translucent', 0.36);
  d.step('舷窗与金属面', '金色窗框包住深蓝玻璃，用一块浅蓝反射表示弧面；船身的面板和尾翼折线保持同样的透视方向。');
  panel(ellipse(0, -34, 60, 60), c[2]);
  panel(ellipse(0, -34, 45, 45), c[0]);
  solid(place(curve([-35, -46], [[-19, -78], [19, -78], [36, -48]], [[7, -60], [-3, -14], [-32, -13]], [[-40, -23], [-40, -36], [-35, -46]])), c[3], 6);
  d.stroke(place(curve([-23, -51], [[-16, -65], [-2, -68], [12, -64]])), c[5], 7, 'ink');
  panel([[-25, 62], [25, 62], [25, 105], [-25, 105], [-25, 62]], c[5], t.shade);
  d.stroke(place([[-11, 77], [10, 77]]), c[0], 4, 'ink');
  d.stroke(place([[-115, 142], [-151, 196]]), c[0], 5, 'ink', 0.6);
  d.stroke(place([[115, 142], [151, 196]]), c[0], 5, 'ink', 0.6);
  d.step('三层尾焰', '尾焰从喷口连着拉出：外层橙红、中层金黄、芯部接近白色，三层都收向同一个尖端。');
  const flame = curve([-37, 210], [[-64, 292], [-34, 370], [0, 458]], [[34, 370], [64, 292], [37, 210]], [[14, 222], [-14, 222], [-37, 210]]);
  solid(place(flame), '#EA8258', 11);
  solid(place(curve([-25, 211], [[-40, 283], [-24, 346], [0, 419]], [[24, 346], [40, 283], [25, 211]], [[9, 220], [-9, 220], [-25, 211]])), c[2], 9);
  solid(place(curve([-12, 213], [[-21, 265], [-11, 304], [0, 354]], [[11, 304], [21, 265], [12, 213]], [[4, 217], [-4, 217], [-12, 213]])), c[5], 7);
  d.stroke(place(curve([0, 219], [[0, 252], [1, 302], [0, 346]])), c[5], 10, 'glow', 0.5);
  d.step('边缘的冷暖光', '冷光只沿船身朝外的一侧，暖光只贴着喷口；保持大块白色船身清楚，让视线沿尾焰一路走到尖鼻。');
  d.stroke(place(curve([-87, -105], [[-94, -23], [-88, 72], [-75, 119]])), c[3], 7, 'translucent', 0.7);
  d.stroke(place(curve([-88, -90], [[-92, -32], [-89, 21], [-85, 57]])), c[5], 3, 'glow', 0.4);
  d.stroke(place([[-32, 197], [32, 197]]), c[2], 5, 'glow', 0.65);
  d.stroke(place(curve([106, 158], [[124, 174], [143, 191], [160, 204]])), c[2], 4, 'ink', 0.65);
};

// ───────────────────────────── 小金鱼 ─────────────────────────────

/** 进阶：池塘侧游。鱼横向游过画面中部，水草、石头和气泡把池塘交代清楚。 */
const fishAdvanced: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  d.step('水色分层', '水面亮、水底暗，横着扫三层；左上角留一条水面反光。');
  washField(d, c[4], 0.42, -40, 1050, 122, 164);
  washField(d, c[5], 0.36, -40, 320, 110, 152);
  washField(d, c[0], 0.36, 740, 1060, 116, 166);
  d.stroke(curve([-20, 180], [[286, 140], [660, 226], [1020, 156]]), c[5], 22, 'glow', 0.5, true);
  d.step('水草和石头', '水草从两边往上长，中间留给鱼；池底压两三块石头。');
  for (let i = 0; i < 4; i++) d.stroke(curve([96 + i * 56, 1010], [[60 + i * 62, 890], [140 + i * 62, 790], [100 + i * 58, 680]]), c[3], 16, 'ink');
  for (let i = 0; i < 3; i++) d.stroke(curve([912 + i * 40, 1010], [[880 + i * 40, 900], [948 + i * 36, 810], [896 + i * 38, 710]]), c[3], 14, 'ink');
  d.leaf(120, 850, 96, -66); d.leaf(196, 760, -92, -74); d.leaf(930, 830, -98, -70);
  d.shape(ellipse(330, 952, 128, 54), c[0]);
  d.shape(ellipse(606, 978, 164, 60), c[3]);
  d.shape(ellipse(828, 946, 104, 46), c[0]);
  d.step('鱼身大形', '身体是一个横椭圆，头在左、尾在右；尾鳍分成三片，像飘起来的丝带。');
  d.shape(ellipse(486, 500, 226, 146), c[1]);
  d.shape(curve([696, 500], [[822, 374], [928, 372], [950, 454]], [[898, 534], [884, 634], [954, 706]], [[836, 684], [750, 602], [696, 552]]), c[2]);
  d.stroke(curve([744, 460], [[844, 412], [908, 420], [934, 462]]), c[1], 7, 'ink', 0.6);
  d.stroke(curve([750, 546], [[844, 602], [906, 628], [942, 654]]), c[1], 7, 'ink', 0.6);
  d.step('鳍、鳃和眼睛', '背鳍在背上、腹鳍在鳃后，都是半透明的薄片；眼睛靠前，留一块高光。');
  d.shape(curve([434, 362], [[444, 258], [558, 246], [612, 340]], [[546, 348], [478, 356], [434, 362]]), c[2]);
  d.stroke(curve([468, 372], [[490, 322], [544, 306], [584, 340]]), c[1], 8, 'ink', 0.6);
  d.stroke(curve([516, 600], [[546, 716], [648, 774], [702, 736]], [[674, 682], [604, 626], [516, 600]]), c[2], 30, 'translucent', 0.5);
  d.stroke(curve([414, 420], [[448, 502], [448, 576], [410, 632]]), c[0], 9, 'ink');
  d.shape(ellipse(344, 454, 46, 50), c[5], c[0]);
  d.dot(336, 454, 30, c[0]); d.dot(326, 442, 9, c[5]);
  d.stroke(curve([284, 532], [[302, 556], [328, 556], [342, 538]]), c[0], 7, 'ink');
  for (let i = 0; i < 6; i++) d.stroke(ellipse(448 + i * 46, 490 + Math.sin(i * 1.2) * 12, 24, 34, -1.05, 1.05), c[0], 5, 'ink', 0.55);
  d.stroke(curve([380, 420], [[470, 382], [580, 392], [660, 452]]), t.light, 16, 'translucent', 0.4);
  d.step('气泡和光斑', '气泡从水草边往上冒，越往上越大；水面再补几段碎光。');
  [[196, 560, 22], [232, 452, 28], [206, 340, 34], [818, 620, 18], [854, 520, 24]].forEach(([x, y, s], i) => d.stroke(ellipse(x, y, s, s), c[5], 7, 'glow', 0.7 - i * 0.05));
  for (let i = 0; i < 4; i++) d.stroke(curve([300 + i * 120, 236 - i * 8], [[360 + i * 120, 224 - i * 8], [420 + i * 120, 240 - i * 8], [470 + i * 120, 228 - i * 8]]), c[5], 8, 'glow', 0.4, true);
};

/** 艺术：俯看荷池中的长尾金鱼。暖色鱼身与两片舒展的尾鳍，落在深蓝静水和荷叶之间。 */
const fishArt: Builder = d => {
  const c = d.theme.colors, t = d.theme;
  const water = '#032B44', rim = '#7FD6D0';
  d.drawing.background = water;
  const place = (points: XY[]): XY[] => points.map(([u, v]) => [438 + u * 0.868 - v * 0.497, 430 + u * 0.497 + v * 0.868]);
  const solid = (points: XY[], color: string) => { d.fill(points, color); d.stroke(points, color, 10, 'pen', 1, true); };
  const fin = (points: XY[], color: string) => { const p = place(points); solid(p, color); d.stroke(p, t.shade, 3.5, 'ink'); };
  const pad = (x: number, y: number, rx: number, ry: number, start: number) => {
    const edge = ellipse(x, y, rx, ry, start, start + TAU - 0.54);
    const points: XY[] = [[x, y], ...edge, [x, y]];
    solid(points, c[3]); d.stroke(points, c[0], 5, 'ink');
    d.stroke(edge.slice(5, 28), rim, 5, 'translucent', 0.55);
    for (let i = 0; i < 5; i++) {
      const a = start + 0.8 + i * 0.98;
      d.stroke(curve([x, y], [[x + Math.cos(a) * rx * 0.25, y + Math.sin(a) * ry * 0.4], [x + Math.cos(a) * rx * 0.6, y + Math.sin(a) * ry * 0.66], [x + Math.cos(a) * rx * 0.82, y + Math.sin(a) * ry * 0.82]]), rim, 3, 'ink', 0.4);
    }
  };
  d.step('一池静水', '俯视画面先留出整块深蓝水面，只用几段轻轻的弧线交代水纹，中间留给金鱼。');
  d.stroke(ellipse(482, 497, 373, 282, 3.45, 5.02), c[4], 8, 'translucent', 0.17, true);
  d.stroke(ellipse(482, 497, 412, 316, 0.2, 1.73), c[4], 6, 'translucent', 0.18, true);
  d.stroke(ellipse(768, 211, 177, 130, -1.6, 0.6), rim, 4, 'ink', 0.3, true);
  d.step('水面上的荷叶', '两片荷叶一近一远，让鱼从中间穿过去；叶脉从缺口内侧散开，边缘只留少量反光。');
  pad(789, 216, 139, 111, 2.38);
  pad(157, 803, 139, 102, -0.55);
  d.stroke(ellipse(157, 810, 160, 117, 0.4, 2.6), c[4], 4, 'ink', 0.26, true);
  d.step('舒展的双尾', '尾巴从同一个细尾柄分成上下两片，外缘像展开的绸缎；先画它，身体稍后压住根部。');
  const upper = curve([125, 0], [[171, -76], [241, -181], [340, -179]], [[332, -132], [289, -102], [313, -63]], [[259, -50], [218, 18], [151, 17]], [[141, 13], [132, 8], [125, 0]]);
  const lower = curve([130, 2], [[197, 15], [318, 90], [349, 214]], [[290, 196], [269, 157], [232, 167]], [[209, 103], [145, 79], [130, 2]]);
  fin(upper, c[2]); fin(lower, c[2]);
  solid(place(curve([156, -4], [[226, -87], [264, -139], [324, -161]], [[272, -94], [220, -28], [156, -4]])), c[1]);
  solid(place(curve([158, 30], [[226, 59], [294, 133], [333, 194]], [[269, 148], [203, 86], [158, 30]])), c[1]);
  d.step('俯看的鱼身', '头部圆、腹部饱满，往后收成细尾柄；橙金色做底，背上的红斑顺着身体延伸。');
  const body = curve([-176, 0], [[-180, -75], [-88, -115], [19, -86]], [[86, -68], [116, -29], [153, 0]], [[130, 32], [90, 74], [17, 93]], [[-88, 122], [-178, 74], [-176, 0]]);
  solid(place(body), c[2]); d.stroke(place(body), t.shade, 5, 'ink');
  solid(place(curve([-150, -24], [[-130, -70], [-50, -91], [19, -65]], [[71, -44], [84, -12], [132, 0]], [[69, 10], [38, -3], [-10, 18]], [[-72, 27], [-118, 2], [-150, -24]])), c[1]);
  d.stroke(place(curve([-148, 37], [[-91, 93], [35, 79], [104, 25]])), t.shade, 22, 'translucent', 0.32);
  d.step('胸鳍与鳍脉', '两片胸鳍接在头后侧，长尾里的鳍脉都从尾柄出发，弯向各自的外缘。');
  fin(curve([-62, -80], [[-73, -117], [-44, -156], [33, -167]], [[25, -125], [-9, -98], [-62, -80]]), c[2]);
  fin(curve([-60, 83], [[-62, 128], [-19, 165], [46, 163]], [[17, 123], [-22, 94], [-60, 83]]), c[2]);
  [-1, 1].forEach(side => {
    for (let i = 0; i < 3; i++) d.stroke(place(curve([-55, side * 86], [[-36 + i * 9, side * 108], [-20 + i * 15, side * 135], [4 + i * 12, side * 149]])), t.shade, 3.5, 'ink', 0.65);
  });
  for (let i = 0; i < 4; i++) {
    d.stroke(place(curve([151, 2], [[200, -35 - i * 14], [240 + i * 16, -74 - i * 22], [299 + i * 9, -82 - i * 26]])), t.shade, 3.5, 'ink', 0.7);
    d.stroke(place(curve([151, 19], [[204, 50 + i * 9], [244 + i * 15, 94 + i * 18], [257 + i * 24, 154 + i * 14]])), t.shade, 3.5, 'ink', 0.7);
  }
  d.step('眼睛和鳞片', '俯视时两只眼睛分列头部两侧，嘴落在前端；鳞片只沿腹部排三行，留出光滑的背。');
  [-1, 1].forEach(side => {
    const eye = place(ellipse(-122, side * 47, 13, 16));
    d.fill(eye, water); d.stroke(eye, water, 4, 'ink');
    const shine = place([[-125, side * 47 - 4]])[0]; d.dot(shine[0], shine[1], 5, c[5]);
  });
  d.stroke(place(curve([-167, -17], [[-159, -6], [-159, 6], [-168, 17]])), '#C0693C', 4, 'ink');
  d.stroke(place(curve([-86, -68], [[-57, -47], [-58, -23], [-71, -9]])), t.shade, 4, 'ink', 0.6);
  d.stroke(place(curve([-86, 74], [[-58, 54], [-57, 35], [-70, 20]])), t.shade, 4, 'ink', 0.6);
  for (let row = 0; row < 3; row++) for (let col = 0; col < 4; col++) {
    const x = -49 + col * 35 + (row % 2) * 11, y = 27 + row * 15 - col * 6;
    d.stroke(place(ellipse(x, y, 13, 11, -1.2, 1.2)), '#C0693C', 2.5, 'ink', 0.38);
  }
  d.step('薄鳍与水面的反光', '亮笔只贴着鱼背和尾鳍的边，水纹围着鱼游开的方向展开，留住深水与暖色金鱼的对比。');
  d.stroke(place(curve([-92, -48], [[-53, -66], [-12, -56], [20, -37]])), t.light, 12, 'translucent', 0.65);
  d.stroke(place(curve([183, -57], [[242, -121], [293, -160], [331, -169]])), t.light, 5, 'ink', 0.85);
  d.stroke(place(curve([188, 60], [[235, 86], [294, 151], [336, 198]])), t.light, 5, 'ink', 0.75);
  d.stroke(ellipse(342, 365, 165, 125, 3.43, 4.92), rim, 4, 'ink', 0.45, true);
  d.stroke(ellipse(610, 654, 205, 166, 0.56, 1.9), rim, 4, 'ink', 0.4, true);
};

export function independentLesson(subject: Subject, level: LessonLevel) {
  const build: Builder | undefined = level === 1 ? ADVANCED[subject] : level === 2 ? ART[subject] : undefined;
  if (!build) throw new Error('入门版仍由 drawing-examples-data.ts 生成');
  const d = new Lesson(subject, level);
  if (level === 2) d.drawing.background = d.theme.paper;
  build(d);
  const meta = META[subject];
  return d.done(subject, meta.title, level === 1 ? meta.adv : meta.art, meta.keywords);
}

const META: Record<Subject, { title: string; adv: string; art: string; keywords: string[] }> = {
  cat: { title: '小猫', adv: '团身午睡 · 软垫与虎斑', art: '月窗仰望 · 侧脸与暖光', keywords: ['猫', '动物', '宠物', '可爱', '脸部'] },
  panda: { title: '熊猫', adv: '抱竹用餐 · 双爪的遮挡', art: '树上小憩 · 斜枝与薄雾', keywords: ['大熊猫', '动物', '竹子', '黑白', '国宝'] },
  sunflower: { title: '向日葵', adv: '青釉陶壶 · 向阳花冠', art: '逆光特写 · 花瓣出画', keywords: ['花', '植物', '花朵', '黄色', '盆栽'] },
  strawberry: { title: '草莓', adv: '两颗鲜果 · 白瓷小碟', art: '花下红果 · 果园肖像', keywords: ['水果', '食物', '红色', '甜点', '果实'] },
  icecream: { title: '冰淇淋', adv: '三球甜筒 · 错位堆叠', art: '融化一杯 · 冷底暖光', keywords: ['冰激凌', '食物', '甜点', '奶油', '夏天'] },
  house: { title: '小房子', adv: '花园小屋 · 两面墙的透视', art: '暮色小屋 · 窗里灯火', keywords: ['房子', '建筑', '家', '风景', '乡村'] },
  boat: { title: '帆船', adv: '白昼航行 · 浪与帆', art: '逆光剪影 · 落日航路', keywords: ['船', '大海', '海边', '旅行', '日落'] },
  balloon: { title: '热气球', adv: '近景分瓣 · 云上高度', art: '暮色远行 · 云海星河', keywords: ['气球', '天空', '旅行', '风景', '云'] },
  rocket: { title: '小火箭', adv: '穿出云层 · 三层尾焰', art: '掠过蓝星 · 斜轴飞行', keywords: ['火箭', '太空', '星空', '宇宙', '科技'] },
  fish: { title: '小金鱼', adv: '池塘侧游 · 水草石影', art: '俯看荷池 · 飘尾金鱼', keywords: ['金鱼', '鱼', '动物', '水族', '池塘'] },
};

const ADVANCED: Record<Subject, Builder> = {
  cat: catAdvanced, panda: pandaAdvanced, sunflower: sunflowerAdvanced, strawberry: strawberryAdvanced,
  icecream: icecreamAdvanced, house: houseAdvanced, boat: boatAdvanced, balloon: balloonAdvanced,
  rocket: rocketAdvanced, fish: fishAdvanced,
};
const ART: Record<Subject, Builder> = {
  cat: catArt, panda: pandaArt, sunflower: sunflowerArt, strawberry: strawberryArt, icecream: icecreamArt,
  house: houseArt, boat: boatArt, balloon: balloonArt, rocket: rocketArt, fish: fishArt,
};
